import React, { useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, Target, DollarSign, Star, MapPin, Eye, BarChart3, Users, CheckCircle, ArrowRight, Mail, Search, Calculator, FileDown } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'sierrascala.com' || window.location.hostname === 'www.sierrascala.com'
  ? ''
  : process.env.REACT_APP_BACKEND_URL;

const ROICalculator = () => {
  const [inputs, setInputs] = useState({
    avgCheck: 21,
    monthlyVisitors: 800,
    currentConversion: 3,
    onlineOrdersMonthly: 130,
    thirdPartyPct: 60,
    commissionRate: 25,
    adBudgetDaily: 15,
    loyaltyLiftPct: 15,
  });

  const set = (key, val) => setInputs(p => ({ ...p, [key]: Number(val) }));

  // Calculations
  const currentOnlineRevenue = inputs.onlineOrdersMonthly * inputs.avgCheck;
  const thirdPartyOrders = Math.round(inputs.onlineOrdersMonthly * inputs.thirdPartyPct / 100);
  const currentCommissionLoss = thirdPartyOrders * inputs.avgCheck * inputs.commissionRate / 100;

  // After plan
  const newConversion = Math.min(inputs.currentConversion * 2, 12);
  const newVisitorsFromSEO = Math.round(inputs.monthlyVisitors * 3);
  const newOrdersFromConversion = Math.round(newVisitorsFromSEO * newConversion / 100);
  const recoveredOrders = Math.round(thirdPartyOrders * 0.5);
  const recoveredMargin = recoveredOrders * inputs.avgCheck * inputs.commissionRate / 100;
  const adBudgetMonthly = inputs.adBudgetDaily * 30;
  const geoFencingOrders = Math.round(adBudgetMonthly / 4);
  const loyaltyLiftOrders = Math.round(inputs.onlineOrdersMonthly * inputs.loyaltyLiftPct / 100);
  const totalNewOrders = recoveredOrders + geoFencingOrders + loyaltyLiftOrders;
  const totalNewRevenue = totalNewOrders * inputs.avgCheck;
  const totalMonthlyCost = adBudgetMonthly;
  const netMonthlyLift = totalNewRevenue + recoveredMargin - totalMonthlyCost;
  const annualLift = netMonthlyLift * 12;

  const fmt = (n) => n >= 1000 ? `$${(n/1000).toFixed(1)}K` : `$${Math.round(n).toLocaleString()}`;

  return (
    <section className="py-12 px-6" data-testid="roi-calculator">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start gap-3 mb-6">
          <Calculator className="text-gray-900 mt-1" size={20} />
          <div>
            <h2 className="text-xl font-bold text-gray-900">ROI Calculator — What This Plan Is Worth</h2>
            <p className="text-gray-500 text-sm mt-1">Adjust the inputs to see estimated monthly revenue lift from the full strategy.</p>
          </div>
        </div>

        <p className="text-gray-700 text-sm leading-relaxed mb-6">
          This isn't a magic number generator — it's a conservative model based on Lily's actual data. Adjust the sliders to match your assumptions. The math is transparent: you can see exactly where each dollar comes from.
        </p>

        {/* Input Sliders */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h3 className="font-bold text-gray-900 text-sm mb-4">Your Numbers</h3>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            {[
              { key: 'avgCheck', label: 'Average check size', min: 10, max: 50, step: 1, prefix: '$' },
              { key: 'monthlyVisitors', label: 'Current monthly website visitors', min: 200, max: 5000, step: 50 },
              { key: 'currentConversion', label: 'Current website → order rate', min: 1, max: 10, step: 0.5, suffix: '%' },
              { key: 'onlineOrdersMonthly', label: 'Online orders per month (all platforms)', min: 50, max: 500, step: 10 },
              { key: 'thirdPartyPct', label: '% of online orders via DoorDash/UberEats', min: 10, max: 90, step: 5, suffix: '%' },
              { key: 'commissionRate', label: 'Third-party commission rate', min: 15, max: 35, step: 1, suffix: '%' },
              { key: 'adBudgetDaily', label: 'Daily ad budget (geo-fence + retargeting)', min: 5, max: 50, step: 1, prefix: '$' },
              { key: 'loyaltyLiftPct', label: 'Expected loyalty repeat lift', min: 5, max: 30, step: 1, suffix: '%' },
            ].map(({ key, label, min, max, step, prefix, suffix }) => (
              <div key={key}>
                <div className="flex justify-between mb-1">
                  <label className="text-xs text-gray-600">{label}</label>
                  <span className="text-xs font-bold text-gray-900">{prefix || ''}{inputs[key]}{suffix || ''}</span>
                </div>
                <input
                  type="range" min={min} max={max} step={step} value={inputs[key]}
                  onChange={e => set(key, e.target.value)}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                  data-testid={`roi-slider-${key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Current State */}
          <div className="bg-white rounded-xl border border-red-200 p-5">
            <h3 className="font-bold text-red-700 text-sm mb-3">Current State (Before Plan)</h3>
            <div className="space-y-2.5">
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Online revenue (all platforms)</span>
                <span className="font-semibold text-gray-900">{fmt(currentOnlineRevenue)}/mo</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Orders going to DoorDash/Uber</span>
                <span className="font-semibold text-red-600">{thirdPartyOrders} orders/mo</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Commission paid to 3rd parties</span>
                <span className="font-semibold text-red-600">-{fmt(currentCommissionLoss)}/mo</span>
              </div>
              <div className="flex justify-between text-xs border-t border-gray-100 pt-2">
                <span className="text-gray-600">Website conversion rate</span>
                <span className="font-semibold text-gray-900">{inputs.currentConversion}%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Monthly website visitors</span>
                <span className="font-semibold text-gray-900">{inputs.monthlyVisitors.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* After Plan */}
          <div className="bg-white rounded-xl border border-green-200 p-5">
            <h3 className="font-bold text-green-700 text-sm mb-3">After Plan (Conservative Estimate)</h3>
            <div className="space-y-2.5">
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Orders recovered from 3rd party → direct</span>
                <span className="font-semibold text-green-700">+{recoveredOrders} orders/mo</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Commission margin saved</span>
                <span className="font-semibold text-green-700">+{fmt(recoveredMargin)}/mo</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">New orders from geo-fencing + retargeting</span>
                <span className="font-semibold text-green-700">+{geoFencingOrders} orders/mo</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Repeat orders from loyalty lift</span>
                <span className="font-semibold text-green-700">+{loyaltyLiftOrders} orders/mo</span>
              </div>
              <div className="flex justify-between text-xs border-t border-gray-100 pt-2">
                <span className="text-gray-600">Est. new website visitors (SEO + content)</span>
                <span className="font-semibold text-gray-900">{newVisitorsFromSEO.toLocaleString()}/mo</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Ad spend (geo-fence + retargeting)</span>
                <span className="font-semibold text-red-600">-{fmt(adBudgetMonthly)}/mo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Line */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h3 className="font-bold text-gray-900 text-sm mb-4">Bottom Line</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-700">+{totalNewOrders}</p>
              <p className="text-[10px] text-gray-500 mt-1">New orders / month</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-700">+{fmt(totalNewRevenue)}</p>
              <p className="text-[10px] text-gray-500 mt-1">New revenue / month</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{fmt(netMonthlyLift)}</p>
              <p className="text-[10px] text-gray-500 mt-1">Net monthly lift</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{fmt(annualLift)}</p>
              <p className="text-[10px] text-gray-500 mt-1">Projected annual impact</p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-gray-800 text-sm leading-relaxed">
            <strong>How to read this:</strong> These are conservative estimates. The "recovered orders" assume you pull back 50% of third-party volume to direct ordering — the real number could be higher with aggressive "ORDERDIRECT" promotion. The geo-fencing estimate assumes $4 cost per new order. The loyalty lift assumes a modest repeat-visit bump. None of this includes the organic traffic growth from SEO, schema, and city pages — which compounds over 6-12 months and costs nothing beyond the initial setup.
          </p>
        </div>
      </div>
    </section>
  );
};

const ToastStrategyPage = () => {
  const exportPDF = useCallback(() => {
    window.print();
  }, []);

  return (
    <div className="min-h-screen bg-white pt-11" data-testid="toast-strategy">

      {/* Nav Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-700 py-2.5 px-5 flex items-center justify-center gap-4" style={{ fontFamily: 'Inter, -apple-system, sans-serif' }}>
        <span className="text-gray-500 text-xs hidden sm:inline">Sierra Scala</span>
        <a href="/lilys" className="text-white text-xs px-4 py-1.5 rounded-full hover:bg-white/10 transition-colors">Slide Deck</a>
        <span className="text-amber-500 text-xs font-semibold px-4 py-1.5 bg-amber-500/10 rounded-full">Strategic Analysis</span>
        <a href="/" className="text-white text-xs px-4 py-1.5 rounded-full hover:bg-white/10 transition-colors">Portfolio</a>
      </nav>

      {/* Header */}
      <header className="border-b border-gray-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Toast Case Study Exercise</p>
            <h1 className="text-lg font-bold text-gray-900">Lily's Silver Spoon — Strategic Analysis</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/lilys" className="text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-full px-3 py-1.5 hover:border-gray-300 transition-colors" data-testid="header-deck-link">View Slide Deck</a>
            <a href="/Lilys_Silver_Spoon_Case_Study_Sierra_Scala.pptx" download className="text-xs font-medium text-amber-700 hover:text-amber-900 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 hover:bg-amber-100 transition-colors" data-testid="header-pptx-link">Download .pptx</a>
            <button onClick={exportPDF} className="text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-full px-3 py-1.5 hover:border-gray-300 transition-colors flex items-center gap-1.5" data-testid="export-pdf-btn">
              <FileDown size={12} />
              Save as PDF
            </button>
          </div>
        </div>
      </header>

      {/* Intro */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-500 text-sm leading-relaxed max-w-3xl">
            Before building a marketing plan, I sat with the data. Not to run it through a formula — but to understand what's actually happening at Lily's. Here's what stood out to me, and why it changes the strategy.
          </p>
        </div>
      </section>

      {/* ═══ INSIGHT 1: % vs $ MARGINS ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <DollarSign className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Percentage margins don't pay rent. Dollar margins do.</h2>
              <p className="text-gray-500 text-sm mt-1">The first thing that jumped out when I looked at the menu data.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            The case study lists gross margin percentages for each item. At first glance, you'd think Lily's should push the Seasonal Harvest Salad (75%) and the Iced Latte (80%) — they have the highest margins. But that's a trap.
          </p>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Item</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Price</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Margin %</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-900 bg-amber-50">$ per Plate</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { item: 'Iced Latte', price: '~$5.50', pct: '80%', dollar: '$4.40', highlight: false },
                  { item: 'Seasonal Harvest Salad', price: '~$8.95', pct: '75%', dollar: '$6.71', highlight: false },
                  { item: 'Mac & Cheese', price: '~$12', pct: '70%', dollar: '$8.40', highlight: false },
                  { item: 'Chocolate Lava Cake', price: '~$7.95', pct: '72%', dollar: '$5.72', highlight: false },
                  { item: 'Classic Cheeseburger', price: '~$15', pct: '65%', dollar: '$9.75', highlight: true },
                  { item: 'Steak Frites', price: '~$24', pct: '58%', dollar: '$13.92', highlight: true },
                  { item: 'Roasted Half Chicken', price: '~$19', pct: '60%', dollar: '$11.40', highlight: true },
                  { item: 'Grilled Salmon', price: '~$24.95', pct: '55%', dollar: '$13.72', highlight: true },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-gray-100 ${row.highlight ? 'bg-green-50/50' : ''}`}>
                    <td className="px-4 py-2.5 text-gray-800">{row.item}</td>
                    <td className="px-4 py-2.5 text-center text-gray-600">{row.price}</td>
                    <td className="px-4 py-2.5 text-center text-gray-600">{row.pct}</td>
                    <td className={`px-4 py-2.5 text-center font-semibold ${row.highlight ? 'text-green-700 bg-green-50' : 'text-gray-600'}`}>{row.dollar}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>The insight:</strong> Steak Frites at 58% margin puts <strong>$13.92 in your pocket per plate</strong>. The Iced Latte at 80% margin puts $4.40. The Cheeseburger at 65% beats the Salad at 75% by over $3 per order.
            </p>
            <p className="text-gray-700 text-sm mt-3 leading-relaxed">
              <strong>What this means for the plan:</strong> Every promotion, every upsell prompt on the POS, every "Chef's Pick" highlight on the menu should be weighted by dollar margin — not percentage. We want servers trained to suggest the Roasted Chicken add-on, not default to recommending the salad. The marketing should hero the burger, the steak, the salmon — the items that actually move the P&L.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ INSIGHT 2: ONLINE ORDERING COLLAPSE ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <TrendingDown className="text-red-600 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Your online ordering is collapsing. DoorDash is eating it.</h2>
              <p className="text-gray-500 text-sm mt-1">This is the most expensive trend in the data.</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Month 1</p>
              <p className="text-lg font-bold text-gray-900">$7,155</p>
              <p className="text-xs text-gray-500">1st Party Online</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Month 2</p>
              <p className="text-lg font-bold text-amber-600">$5,839</p>
              <p className="text-xs text-gray-500">1st Party Online</p>
            </div>
            <div className="bg-white rounded-xl border border-red-200 p-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Month 3</p>
              <p className="text-lg font-bold text-red-600">$3,970</p>
              <p className="text-xs text-gray-500">1st Party Online</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Online ordering through Lily's own platform dropped <strong>45% in three months</strong> ($7,155 → $3,970). In that same period, DoorDash/UberEats revenue jumped from $2,385 to $5,955 — a 150% increase. The customers didn't stop ordering. They just moved to a platform that takes 15-30% commission.
          </p>
          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            That's roughly <strong>$900-1,800/mo in commission fees</strong> that could be margin if those orders came direct. Over a year, that's $10K-$20K walking out the door.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>What this means for the plan:</strong> Priority one is recapturing those online orders onto Lily's own platform. That means SMS/email campaigns with a direct ordering link, a "skip the app" promo (10% off first direct order), and making sure the ordering page actually works well on mobile. The Toast Online Ordering integration is the hero here — every marketing touchpoint should link directly to it.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ INSIGHT 2B: ONLINE ORDERING PAGE UX ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Eye className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">And your online ordering page isn't helping.</h2>
              <p className="text-gray-500 text-sm mt-1">I actually looked at it. Here's what a customer sees.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            If you're trying to win customers back from DoorDash, the ordering page has to be <em>better</em> than DoorDash — not worse. Right now, it's worse. Three things jumped out immediately:
          </p>

          <div className="space-y-4 mb-6">
            <div className="bg-white rounded-xl border border-red-200 p-5">
              <div className="flex items-start gap-3">
                <span className="text-red-500 font-bold text-lg mt-0.5">1</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">The "Entrees" category is highlighted — but Appetizers and Desserts are also showing</h3>
                  <p className="text-gray-600 text-xs mt-1">The navigation says you're looking at Entrees, but the page displays items from all categories at once. That's confusing. A customer looking for a quick appetizer has to scroll past entrees. Someone ordering dessert sees sandwich prices first. The categories exist but don't actually filter. It defeats the purpose of having navigation.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-red-200 p-5">
              <div className="flex items-start gap-3">
                <span className="text-red-500 font-bold text-lg mt-0.5">2</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">No photos on any menu item</h3>
                  <p className="text-gray-600 text-xs mt-1">DoorDash shows a photo of every dish. Lily's ordering page shows text and a price. When people order food online, they eat with their eyes first. A $20 Grilled Salmon with a photo converts. A text line that says "Grilled Salmon — $20.00" doesn't. This is the single biggest reason customers default to DoorDash — the experience <em>looks</em> better, even if the food and price are the same.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-red-200 p-5">
              <div className="flex items-start gap-3">
                <span className="text-red-500 font-bold text-lg mt-0.5">3</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">No descriptions on any item</h3>
                  <p className="text-gray-600 text-xs mt-1">"Sandwich — $9.99." What sandwich? Is it a club? A Reuben? A pressed Cuban? Without a description, there's no appetite appeal and no way to justify the price. A two-line description — <em>"House-smoked turkey, bacon, avocado, arugula, lemon aioli on toasted sourdough"</em> — turns a $10 question mark into an easy yes.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>The fix:</strong> Photo shoot of every menu item (I'd do this in week one with my camera and Lightroom). Write short, appetizing descriptions for each item. Fix the category nav so it actually filters. These three changes alone would close the gap between Lily's ordering page and DoorDash — and that's what brings those orders (and their margins) back in-house.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ INSIGHT 3: GBP MISCATEGORIZED ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <MapPin className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Google thinks you're fine dining. You're not. That's a problem.</h2>
              <p className="text-gray-500 text-sm mt-1">This is costing Lily's customers who would love the restaurant.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            The Google Business Profile lists Lily's Silver Spoon as <strong>"$$$ - Fine dining restaurant"</strong> with 3.8 stars and only 23 reviews. But Lily's is a $21-average neighborhood bistro built on <strong>seasonal farm-to-table ingredients</strong> and comfort food — burgers, mac & cheese, buttermilk pancakes. That's not fine dining. That's the kind of place you go on a Tuesday with your family.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-red-200 p-5">
              <h3 className="font-bold text-red-700 text-sm mb-3">What's Wrong</h3>
              <ul className="space-y-2">
                {[
                  "Listed as \"Fine Dining\" — scares away casual diners and students",
                  "Priced as $$$ — actual average entree is $21 (that's $$)",
                  "Only 23 reviews at 3.8 stars — competitors have 150-300",
                  "Hours not listed, phone not listed, menu not available",
                  "Only 7 photos — stale, not showcasing the food",
                  "No website link visible in the listing",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                    <span className="text-red-400 mt-0.5 flex-shrink-0">&#10005;</span>{item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl border border-green-200 p-5">
              <h3 className="font-bold text-green-700 text-sm mb-3">Quick Fixes (Week 1)</h3>
              <ul className="space-y-2">
                {[
                  "Change category to \"American Restaurant\" or \"Farm-to-Table\"",
                  "Update price level from $$$ to $$",
                  "Add hours, phone, website, and full menu link",
                  "Upload 20+ fresh food photos (hero the burger, chicken, pancakes)",
                  "Start a review generation campaign (goal: 100 reviews in 60 days)",
                  "Enable Google Posts — weekly specials, events, seasonal menu",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                    <CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>Why this matters:</strong> A college student searching "good food near me" at 8pm sees "$$$ Fine Dining" and scrolls past. A family looking for Tuesday dinner sees 3.8 stars with 23 reviews next to a competitor with 4.4 stars and 267 reviews — they pick the competitor. The food might be great, but the digital front door is turning people away before they ever walk in.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ GBP BEFORE / AFTER MOCKUP ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Eye className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Before & After — What Customers Actually See</h2>
              <p className="text-gray-500 text-sm mt-1">Side-by-side: Lily's current Google listing vs. what it should look like after Week 1 fixes.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* BEFORE */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <p className="text-xs font-bold text-red-700 uppercase tracking-wider">Current Listing</p>
              </div>
              <div className="bg-white rounded-xl border-2 border-red-200 overflow-hidden">
                {/* GBP Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-blue-700 text-base">Lily's Silver Spoon</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Fine dining restaurant</p>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">$$$</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-amber-500 text-sm">&#9733;&#9733;&#9733;&#9734;&#9734;</span>
                    <span className="text-xs text-gray-500">3.8 (23 reviews)</span>
                  </div>
                </div>
                {/* GBP Info */}
                <div className="p-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <MapPin size={12} />
                    <span>123 Main Street (address only)</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-red-400">
                    <span className="w-3 h-3 flex items-center justify-center">&#8212;</span>
                    <span className="line-through">Hours not listed</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-red-400">
                    <span className="w-3 h-3 flex items-center justify-center">&#8212;</span>
                    <span className="line-through">Phone not listed</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-red-400">
                    <span className="w-3 h-3 flex items-center justify-center">&#8212;</span>
                    <span className="line-through">Website not linked</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-red-400">
                    <span className="w-3 h-3 flex items-center justify-center">&#8212;</span>
                    <span className="line-through">Menu not available</span>
                  </div>
                </div>
                {/* Photos */}
                <div className="px-4 pb-4">
                  <p className="text-[10px] text-gray-400 mb-2">Photos (7)</p>
                  <div className="flex gap-1">
                    {[1,2,3,4,5,6,7].map(i => (
                      <div key={i} className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-[8px] text-gray-400">old</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Google Posts */}
                <div className="px-4 pb-4">
                  <p className="text-[10px] text-gray-400">Google Posts: None</p>
                </div>
                {/* Order Button */}
                <div className="px-4 pb-4">
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-gray-400">Order link buried / hard to find</p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-red-500 mt-2 text-center font-medium">A college student sees this and scrolls past.</p>
            </div>

            {/* AFTER */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <p className="text-xs font-bold text-green-700 uppercase tracking-wider">After Week 1 Fixes</p>
              </div>
              <div className="bg-white rounded-xl border-2 border-green-200 overflow-hidden">
                {/* GBP Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-blue-700 text-base">Lily's Silver Spoon</h3>
                      <p className="text-xs text-gray-600 mt-0.5">American restaurant &middot; Farm-to-table &middot; Comfort food</p>
                    </div>
                    <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">$$</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-amber-500 text-sm">&#9733;&#9733;&#9733;&#9733;&#9734;</span>
                    <span className="text-xs text-gray-600 font-medium">4.3 (127 reviews)</span>
                  </div>
                </div>
                {/* GBP Info */}
                <div className="p-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <MapPin size={12} className="text-green-600" />
                    <span>123 Main Street, Arlington TX</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <CheckCircle size={12} className="text-green-600" />
                    <span><strong>Open</strong> &middot; Closes 10 PM &middot; Mon-Sun 7AM-10PM</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <CheckCircle size={12} className="text-green-600" />
                    <span>(817) 555-0123</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <CheckCircle size={12} className="text-green-600" />
                    <span className="underline">lilyssilverspoon.com</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <CheckCircle size={12} className="text-green-600" />
                    <span className="underline">View full menu</span>
                  </div>
                </div>
                {/* Photos */}
                <div className="px-4 pb-4">
                  <p className="text-[10px] text-gray-500 mb-2">Photos (45+)</p>
                  <div className="flex gap-1">
                    {['Burger', 'Mac&Ch', 'Interior', 'Pancake', 'Steak', 'Patio', 'Team'].map((label, i) => (
                      <div key={i} className="w-10 h-10 bg-amber-100 rounded flex items-center justify-center border border-amber-200">
                        <span className="text-[7px] text-amber-700 font-medium">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Google Posts */}
                <div className="px-4 pb-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                    <p className="text-[10px] text-green-800 font-medium">Latest Post: "Monday Night — Burger & a Beer, $16"</p>
                    <p className="text-[10px] text-green-600">Posted 2 days ago &middot; Weekly specials every Monday</p>
                  </div>
                </div>
                {/* Order Button */}
                <div className="px-4 pb-4">
                  <div className="bg-blue-600 rounded-lg p-2.5 text-center cursor-pointer hover:bg-blue-700 transition-colors">
                    <p className="text-xs text-white font-bold">Order Online — Direct from Lily's</p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-green-600 mt-2 text-center font-medium">Same restaurant, completely different first impression.</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-6">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>The difference:</strong> Same restaurant. Same food. Same address. But the listing on the right actually tells customers what Lily's is — a $$ comfort-food bistro with 127 reviews, full hours, a menu link, fresh photos, and a direct order button. Every fix shown here can be done in <strong>one afternoon</strong>. The review count takes 60-90 days to build, but the profile cleanup is immediate.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ SEASONAL MENU MOCKUP ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Target className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Seasonal Menu Page — What It Should Look Like</h2>
              <p className="text-gray-500 text-sm mt-1">A rotating menu built around local farms and seasonal ingredients — the content engine that markets itself.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Lily's farm-to-table concept is the single biggest differentiator nobody can see online. A dedicated seasonal menu page turns that into a marketing asset: it gives Google fresh content every month, gives social media a recurring "menu drop" moment, and gives customers a reason to come back and see what's new.
          </p>

          {/* Mockup Browser Frame */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm mb-6">
            {/* Browser chrome */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 ml-3">
                <div className="bg-white rounded-md border border-gray-200 px-3 py-1 text-[10px] text-gray-500 max-w-md">
                  lilyssilverspoon.com/seasonal-menu
                </div>
              </div>
            </div>

            {/* Page Content */}
            <div style={{ background: '#FEF9F3', fontFamily: "'Playfair Display', Georgia, serif" }}>
              {/* Hero Banner */}
              <div className="px-8 pt-10 pb-6 text-center" style={{ borderBottom: '1px solid #E8E0D4' }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: '#C07A3A', fontFamily: "'IBM Plex Sans', sans-serif" }}>Updated monthly &middot; Sourced locally</p>
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#1A1A1A' }}>Winter 2026 Menu</h3>
                <p className="text-sm" style={{ color: '#998E80', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Seasonal ingredients from Harvest Moon Farm, Elm Creek Dairy &amp; Stone Ridge Orchard
                </p>
                <div className="flex justify-center gap-3 mt-4">
                  {['Winter 2026', 'Fall 2025', 'Summer 2025', 'Spring 2025'].map((s, i) => (
                    <button key={i} className={`text-[10px] px-3 py-1.5 rounded-full transition-colors ${i === 0 ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`}
                      style={i === 0 ? { background: '#C07A3A', fontFamily: "'IBM Plex Sans', sans-serif" } : { background: '#F5E6D0', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Menu Grid */}
              <div className="px-8 py-6">
                {/* Farm Partner Banner */}
                <div className="rounded-xl p-4 mb-6 flex items-center gap-4" style={{ background: '#F5E6D0', border: '1px solid #E8E0D4' }}>
                  <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white text-lg" style={{ background: '#B07B56' }}>
                    HM
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: '#C07A3A', fontFamily: "'IBM Plex Sans', sans-serif" }}>This month's featured farm</p>
                    <p className="font-bold text-sm" style={{ color: '#1A1A1A' }}>Harvest Moon Farm &middot; Arlington, TX</p>
                    <p className="text-[10px]" style={{ color: '#998E80', fontFamily: "'IBM Plex Sans', sans-serif" }}>Supplying our winter root vegetables, butternut squash, and fresh herbs since 2019</p>
                  </div>
                </div>

                {/* Starters */}
                <p className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#C07A3A', fontFamily: "'IBM Plex Sans', sans-serif" }}>Starters</p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { name: 'Roasted Butternut Squash Soup', desc: 'Harvest Moon Farm squash, sage brown butter, toasted pepitas', price: '$9', tag: 'NEW', tagColor: '#5E8C61', margin: '74% · $6.66/plate' },
                    { name: 'Winter Harvest Salad', desc: 'Roasted beets, goat cheese, candied walnuts, pomegranate vinaigrette', price: '$12', tag: 'SEASONAL', tagColor: '#C07A3A', margin: '75% · $9.00/plate' },
                    { name: 'Crispy Brussels Sprouts', desc: 'Balsamic glaze, shaved parmesan, Elm Creek honey', price: '$11', tag: null, margin: '73% · $8.03/plate' },
                    { name: 'Loaded Potato Skins', desc: 'Sharp cheddar, applewood bacon, chive sour cream', price: '$10', tag: 'FAN FAVE', tagColor: '#998E80', margin: '71% · $7.10/plate' },
                  ].map((item, i) => (
                    <div key={i} className="rounded-xl p-3.5 flex justify-between items-start" style={{ background: '#FFFFFF', border: '1px solid #E8E0D4' }}>
                      <div className="flex-1 mr-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <p className="font-bold text-xs" style={{ color: '#1A1A1A' }}>{item.name}</p>
                          {item.tag && (
                            <span className="text-[7px] px-1.5 py-0.5 rounded-full text-white font-bold" style={{ background: item.tagColor }}>{item.tag}</span>
                          )}
                        </div>
                        <p className="text-[10px] leading-relaxed" style={{ color: '#998E80', fontFamily: "'IBM Plex Sans', sans-serif" }}>{item.desc}</p>
                      </div>
                      <span className="font-bold text-sm flex-shrink-0" style={{ color: '#C07A3A' }}>{item.price}</span>
                    </div>
                  ))}
                </div>

                {/* Mains */}
                <p className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#C07A3A', fontFamily: "'IBM Plex Sans', sans-serif" }}>Mains</p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { name: 'Braised Short Rib', desc: 'Red wine braise, creamy polenta, roasted root vegetables from Harvest Moon', price: '$26', tag: 'CHEF\'S PICK', tagColor: '#5E8C61', margin: '58% · $13.92/plate' },
                    { name: 'Pan-Seared Salmon', desc: 'Citrus glaze, wild rice pilaf, seasonal greens, lemon butter', price: '$24', tag: 'SEASONAL', tagColor: '#C07A3A', margin: '60% · $14.40/plate' },
                    { name: 'Classic Cheeseburger', desc: 'Hand-formed patty, sharp cheddar, brioche bun, house pickles', price: '$17', tag: 'FAN FAVE', tagColor: '#998E80', margin: '65% · $9.75/plate' },
                    { name: 'Winter Mushroom Risotto', desc: 'Arborio rice, mixed wild mushrooms, truffle oil, parmesan', price: '$19', tag: 'NEW', tagColor: '#5E8C61', margin: '68% · $12.92/plate' },
                    { name: 'Steak Frites', desc: '8oz NY strip, herb butter, hand-cut fries, peppercorn sauce', price: '$28', tag: null, margin: '58% · $13.92/plate' },
                    { name: 'Mac & Cheese', desc: 'Four-cheese blend, panko crust, optional bacon or truffle', price: '$15', tag: 'STUDENT PICK', tagColor: '#C07A3A', margin: '70% · $8.40/plate' },
                  ].map((item, i) => (
                    <div key={i} className="rounded-xl p-3.5 flex justify-between items-start" style={{ background: '#FFFFFF', border: '1px solid #E8E0D4' }}>
                      <div className="flex-1 mr-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <p className="font-bold text-xs" style={{ color: '#1A1A1A' }}>{item.name}</p>
                          {item.tag && (
                            <span className="text-[7px] px-1.5 py-0.5 rounded-full text-white font-bold" style={{ background: item.tagColor }}>{item.tag}</span>
                          )}
                        </div>
                        <p className="text-[10px] leading-relaxed" style={{ color: '#998E80', fontFamily: "'IBM Plex Sans', sans-serif" }}>{item.desc}</p>
                        {item.margin && (
                          <p className="text-[8px] mt-1 font-bold" style={{ color: '#5E8C61', fontFamily: "'IBM Plex Sans', sans-serif" }}>{item.margin}</p>
                        )}
                      </div>
                      <span className="font-bold text-sm flex-shrink-0" style={{ color: '#C07A3A' }}>{item.price}</span>
                    </div>
                  ))}
                </div>

                {/* Desserts + Drinks row */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#C07A3A', fontFamily: "'IBM Plex Sans', sans-serif" }}>Desserts</p>
                    <div className="space-y-2">
                      {[
                        { name: 'Chocolate Lava Cake', desc: 'Molten center, vanilla bean ice cream', price: '$10', tag: 'FREE W/ LOYALTY', tagColor: '#5E8C61', margin: '72% · $5.72/plate — $2.28 cost' },
                        { name: 'Spiced Pear Crumble', desc: 'Stone Ridge orchard pears, oat crumble, caramel', price: '$9', tag: 'SEASONAL', tagColor: '#C07A3A', margin: '70% · $6.30/plate' },
                        { name: 'Buttermilk Pancakes', desc: 'Maple butter, seasonal fruit, whipped cream', price: '$11', tag: null, margin: '68% · $7.48/plate' },
                      ].map((item, i) => (
                        <div key={i} className="rounded-xl p-3 flex justify-between items-start" style={{ background: '#FFFFFF', border: '1px solid #E8E0D4' }}>
                          <div className="flex-1 mr-2">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <p className="font-bold text-[11px]" style={{ color: '#1A1A1A' }}>{item.name}</p>
                              {item.tag && <span className="text-[6px] px-1 py-0.5 rounded-full text-white font-bold" style={{ background: item.tagColor }}>{item.tag}</span>}
                            </div>
                            <p className="text-[9px]" style={{ color: '#998E80', fontFamily: "'IBM Plex Sans', sans-serif" }}>{item.desc}</p>
                            {item.margin && <p className="text-[7px] mt-0.5 font-bold" style={{ color: '#5E8C61', fontFamily: "'IBM Plex Sans', sans-serif" }}>{item.margin}</p>}
                          </div>
                          <span className="font-bold text-xs flex-shrink-0" style={{ color: '#C07A3A' }}>{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#C07A3A', fontFamily: "'IBM Plex Sans', sans-serif" }}>Seasonal Drinks</p>
                    <div className="space-y-2">
                      {[
                        { name: 'Spiced Pumpkin Latte', desc: 'House-made pumpkin spice, oat milk, espresso', price: '$6', tag: 'SEASONAL', tagColor: '#C07A3A', margin: '80% · $4.80/cup' },
                        { name: 'Mulled Cider', desc: 'Stone Ridge apples, cinnamon, star anise', price: '$5', tag: 'NEW', tagColor: '#5E8C61', margin: '78% · $3.90/cup' },
                        { name: 'Classic Iced Latte', desc: 'Double shot, your choice of milk', price: '$5.50', tag: null, margin: '80% · $4.40/cup' },
                      ].map((item, i) => (
                        <div key={i} className="rounded-xl p-3 flex justify-between items-start" style={{ background: '#FFFFFF', border: '1px solid #E8E0D4' }}>
                          <div className="flex-1 mr-2">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <p className="font-bold text-[11px]" style={{ color: '#1A1A1A' }}>{item.name}</p>
                              {item.tag && <span className="text-[6px] px-1 py-0.5 rounded-full text-white font-bold" style={{ background: item.tagColor }}>{item.tag}</span>}
                            </div>
                            <p className="text-[9px]" style={{ color: '#998E80', fontFamily: "'IBM Plex Sans', sans-serif" }}>{item.desc}</p>
                            {item.margin && <p className="text-[7px] mt-0.5 font-bold" style={{ color: '#5E8C61', fontFamily: "'IBM Plex Sans', sans-serif" }}>{item.margin}</p>}
                          </div>
                          <span className="font-bold text-xs flex-shrink-0" style={{ color: '#C07A3A' }}>{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Order CTA */}
                <div className="mt-6 rounded-xl p-4 text-center" style={{ background: '#C07A3A' }}>
                  <p className="text-white font-bold text-sm mb-1">Order direct — keep your loyalty points</p>
                  <p className="text-white/70 text-[10px]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Skip the apps. Free delivery on orders over $30. Use code ORDERDIRECT for 10% off your first order.</p>
                </div>

                {/* Footer */}
                <div className="mt-4 text-center pb-4">
                  <p className="text-[9px]" style={{ color: '#998E80', fontFamily: "'IBM Plex Sans', sans-serif" }}>Menu updated January 2026 &middot; Prices may vary &middot; Ask about allergens &middot; Farm partners may change based on availability</p>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Works */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="font-bold text-gray-900 text-sm mb-2">SEO Engine</p>
              <p className="text-xs text-gray-600">Every monthly menu update is fresh content Google indexes. Season-specific keywords ("winter menu Arlington," "farm-to-table brunch near me") drive long-tail organic traffic year-round. Each season's page stays live as an archive.</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="font-bold text-gray-900 text-sm mb-2">Content Calendar Fuel</p>
              <p className="text-xs text-gray-600">The monthly "menu drop" feeds TikTok, IG Reels, and email campaigns automatically. Feature the farm partner, spotlight new dishes, run a "guess the seasonal ingredient" poll. One page change = a week of content.</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="font-bold text-gray-900 text-sm mb-2">Return Visit Driver</p>
              <p className="text-xs text-gray-600">A rotating menu gives regulars a reason to come back. "What's new this month?" is a more compelling pull than "same menu, same Tuesday." Farm-partner stories add emotional connection competitors can't copy.</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>Implementation note:</strong> This doesn't require a full website rebuild. It's one new page (/seasonal-menu) with a simple template that gets updated monthly. The farm partner section takes 10 minutes to write. The menu items already exist in Toast — just tag the seasonal ones. The margin annotations (% and $ per plate) are internal-only strategy overlays shown here so you can see which items to push — they'd be hidden on the customer-facing page. The season tabs let customers browse previous menus, which Google indexes as separate keyword-rich pages.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ INSIGHT 4: LOYALTY DYING ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Users className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Loyalty went from 25% to 19% in three months. That's a warning sign.</h2>
              <p className="text-gray-500 text-sm mt-1">The program isn't broken — it's just not being activated.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Loyalty share of orders dropped from 25% to 19%. That's a 24% decline in three months. But total sales are still growing (slowly). That tells me new customers are coming in — they're just not joining the loyalty program. The program itself probably isn't the problem. The onboarding is.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>The fix — tabletop QR codes:</strong> Every table gets a simple QR code tent card: <em>"Scan to earn rewards on today's meal."</em> No app download. No long signup form. Just scan, enter your phone number, and you're in. First scan earns a free dessert on the next visit (hero the Chocolate Lava Cake — 72% margin, $5.72 cost to the house, but it drives a full return visit).
            </p>
            <p className="text-gray-700 text-sm mt-3 leading-relaxed">
              For the 4,000 college students nearby: pair the QR code with an Instagram story prompt — <em>"Show us your meal for double loyalty points."</em> That's UGC + loyalty acquisition in one move, and it costs nothing.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ REFERRAL PROGRAM + QR TABLETOP DETAIL ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Users className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Referral Program + QR Tabletop — The Zero-Cost Growth Engine</h2>
              <p className="text-gray-500 text-sm mt-1">Two systems that compound together: every new member becomes a recruiter, every table becomes a signup desk.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* QR Tabletop Detail */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">QR Tabletop Tents</p>
              <p className="text-gray-700 text-xs leading-relaxed mb-4">
                Every table gets a tent card with a QR code. No app download, no email form, no friction. Scan &rarr; enter phone number &rarr; earning points. The goal: capture every dine-in customer into the loyalty system automatically.
              </p>

              {/* Mockup of tent card */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center mb-4">
                <div className="w-20 h-20 bg-white border-2 border-gray-300 rounded-lg mx-auto mb-2 flex items-center justify-center">
                  <div className="grid grid-cols-5 gap-0.5">
                    {Array(25).fill(0).map((_, i) => (
                      <div key={i} className={`w-2.5 h-2.5 rounded-sm ${[0,1,2,3,4,5,9,10,14,15,19,20,21,22,23,24].includes(i) ? 'bg-gray-800' : 'bg-white'}`} />
                    ))}
                  </div>
                </div>
                <p className="text-amber-900 font-bold text-xs mb-0.5">Scan to earn rewards</p>
                <p className="text-amber-700 text-[10px]">No app needed &middot; Takes 10 seconds</p>
                <p className="text-amber-600 text-[10px] font-medium mt-1">First reward: Free Chocolate Lava Cake</p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-bold text-gray-800">How it works at the table:</p>
                {[
                  { step: "1", text: "Guest scans QR code with their phone camera" },
                  { step: "2", text: "Toast loyalty page opens — enter phone number (10 seconds)" },
                  { step: "3", text: "Today's meal starts earning points immediately" },
                  { step: "4", text: "First reward unlocked: free Lava Cake on next visit ($2.28 cost, drives a full return)" },
                  { step: "5", text: "Every future visit: scan at checkout to earn, redeem at the table" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="w-5 h-5 bg-amber-100 text-amber-800 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{item.step}</span>
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] text-gray-700"><strong>Student bonus:</strong> "Show us your meal on IG Stories for double points." Tags the restaurant, generates UGC, and acquires a loyalty member — all in one move.</p>
              </div>
            </div>

            {/* Referral Program */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-3">Bring-a-Friend Referral Program</p>
              <p className="text-gray-700 text-xs leading-relaxed mb-4">
                The cheapest customer acquisition channel that exists. Every loyalty member gets a personal referral code. When a friend uses it, both earn rewards. Word-of-mouth is the only channel that compounds without increasing spend.
              </p>

              {/* How the referral flow works */}
              <div className="space-y-3 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                  <p className="text-green-800 font-bold text-xs mb-1">The offer:</p>
                  <p className="text-green-700 text-xs">"Bring a friend, you both get $5 off your next meal."</p>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-800">How it spreads:</p>
                  {[
                    { who: "Existing member", action: "Shares their unique code (text, IG story, group chat)" },
                    { who: "New friend", action: "Orders using the code — gets $5 off their first direct order" },
                    { who: "Both", action: "Earn 100 bonus loyalty points when the code is redeemed" },
                    { who: "New friend", action: "Gets their own referral code after first visit — cycle repeats" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 w-20 text-center">{item.who}</span>
                      <span className="text-gray-700">{item.action}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Why it's perfect for college */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-bold text-gray-800 mb-2">Why this is perfect for a college market:</p>
                <div className="space-y-1.5">
                  {[
                    "Students share everything in group chats — one referral code reaches 50-100 people instantly",
                    "\"My friend got us $5 off at Lily's\" is more persuasive than any ad you can run",
                    "Dorm-to-dorm word-of-mouth has no ad cost, no CPM, no click-through rate to optimize",
                    "A table of 4 referred students at $21/check = $84 revenue from a $10 referral investment",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[10px] text-gray-700">
                      <CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Economics */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center bg-gray-50 rounded-lg p-2">
                  <p className="text-sm font-bold text-gray-900">$5</p>
                  <p className="text-[9px] text-gray-500">Cost per referral</p>
                </div>
                <div className="text-center bg-gray-50 rounded-lg p-2">
                  <p className="text-sm font-bold text-green-700">$84</p>
                  <p className="text-[9px] text-gray-500">Avg table revenue</p>
                </div>
                <div className="text-center bg-gray-50 rounded-lg p-2">
                  <p className="text-sm font-bold text-amber-600">17x</p>
                  <p className="text-[9px] text-gray-500">Return on referral</p>
                </div>
              </div>
            </div>
          </div>

          {/* Combined flywheel */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">The Flywheel — How QR + Referrals Compound Together</h3>
            <div className="flex flex-col md:flex-row items-center gap-3 justify-center text-center">
              {[
                { step: "Guest dines in", sub: "Sees QR tent card" },
                { step: "Scans QR code", sub: "Joins loyalty (10 sec)" },
                { step: "Earns first points", sub: "Free Lava Cake unlocked" },
                { step: "Gets referral code", sub: "Shares with friends" },
                { step: "Friends visit", sub: "Both earn $5 off" },
                { step: "Friends join loyalty", sub: "Cycle repeats" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 min-w-[120px]">
                    <p className="text-xs font-bold text-gray-900">{item.step}</p>
                    <p className="text-[9px] text-gray-500">{item.sub}</p>
                  </div>
                  {i < 5 && <span className="text-gray-300 text-lg hidden md:inline">&rarr;</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>The math:</strong> If 20% of loyalty members share their referral code and each code brings in just one new customer per month, that's 13 new customers/mo from a base of 663 loyalty members — at a $5 acquisition cost vs $15-25 for a paid ad. Those referred customers convert at 3-5x the rate of cold traffic because they already trust the recommendation. And once they're in the loyalty system, they get their own referral code. That's how you build a word-of-mouth engine that grows without increasing your ad budget.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ INSIGHT 5: SLOW TIMES ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <BarChart3 className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Monday dinner is $1,056. Friday dinner is $4,139. Fill the valleys.</h2>
              <p className="text-gray-500 text-sm mt-1">The data tells you exactly when and where to push.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Monday and Wednesday dinners are the clear weak spots. Saturday breakfast is the weakest morning. These aren't problems — they're opportunities with a known address. You don't need to guess where to focus.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-800 text-sm mb-3">Weakest Dayparts</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Monday Dinner</span><span className="font-semibold text-red-600">$1,056</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Wednesday Dinner</span><span className="font-semibold text-amber-600">$2,217</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Saturday Breakfast</span><span className="font-semibold text-amber-600">$1,100</span></div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-800 text-sm mb-3">Targeted Campaigns</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>Monday:</strong> "Monday Night Burger & a Beer" — $16 combo. Push via SMS at 3pm Monday.</p>
                <p><strong>Wednesday:</strong> "Wine Down Wednesday" — half-price bottles with entree. Email Tuesday night.</p>
                <p><strong>Saturday AM:</strong> "Weekend Pancake Stack" — Instagram Reel of the buttermilk pancakes, Friday night post.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TECHNICAL FOUNDATION ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Target className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Before spending a dollar on ads — fix the foundation.</h2>
              <p className="text-gray-500 text-sm mt-1">None of the campaigns above matter if Google can't properly crawl, index, and understand Lily's website.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            This is the boring stuff nobody wants to talk about, but it's the difference between a site that ranks and one that doesn't. I'd audit and fix all of this in week one — before touching any content or ad spend.
          </p>

          <div className="space-y-4 mb-6">
            {/* Schema */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-2">Schema Markup — Structured Data on Every Page</h3>
              <p className="text-gray-600 text-xs leading-relaxed mb-3">
                Schema is how you tell Google <em>what</em> your business is, not just what your pages say. Without it, Google is guessing. With it, you're eligible for rich snippets, star ratings in search results, and local pack features.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">What I'd implement:</p>
                  <div className="space-y-1.5">
                    {["LocalBusiness + Restaurant schema (name, address, hours, phone, cuisine type)", "Menu schema with item names, descriptions, and prices", "AggregateRating schema (pull in existing review data)", "FAQPage schema on any page with Q&A content", "BreadcrumbList for cleaner search result display", "OpeningHoursSpecification for Google Business Profile sync"].map((item, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700"><CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Why it matters for Lily's:</p>
                  <p className="text-gray-600 text-xs leading-relaxed">Lily's has reviews, hours, a menu, and a physical location — all the data Google wants. But none of it is in structured data format. Adding AggregateRating schema alone could put star ratings next to Lily's in search results — that's the difference between getting clicked and getting scrolled past.</p>
                </div>
              </div>
            </div>

            {/* Indexing & Sitemaps */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-2">Page Indexing, Sitemaps & Search Console</h3>
              <p className="text-gray-600 text-xs leading-relaxed mb-3">
                If Google doesn't know a page exists, it can't rank it. If Bing doesn't know, you're invisible to 10% of search traffic. This is basic hygiene that most small restaurants skip.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-800 mb-1.5">Google Search Console</p>
                  <div className="space-y-1">
                    {["Verify site ownership", "Submit XML sitemap", "Check index coverage (what's indexed, what's not)", "Fix crawl errors and redirect chains", "Monitor Core Web Vitals (page speed)", "Request indexing for new pages immediately"].map((item, i) => (
                      <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-800 mb-1.5">Bing IndexNow</p>
                  <div className="space-y-1">
                    {["Set up Bing Webmaster Tools", "Implement IndexNow protocol for instant Bing indexing", "Submit sitemap to Bing separately", "Any new page or update → Bing knows within minutes", "Covers Bing, Yahoo, DuckDuckGo, and Yandex in one protocol"].map((item, i) => (
                      <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-800 mb-1.5">XML Sitemap</p>
                  <div className="space-y-1">
                    {["Auto-generated sitemap with all pages", "Includes menu pages, location, about, ordering", "Updated automatically when new content is added", "Priority tags on high-value pages (menu, ordering, specials)", "Last-modified dates so crawlers know what changed"].map((item, i) => (
                      <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* GA4 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-2">Google Analytics 4 — Properly Installed on Every Page</h3>
              <p className="text-gray-600 text-xs leading-relaxed mb-3">
                You can't improve what you don't measure. GA4 needs to be on every single page — not just the homepage — and it needs to be tracking the right events.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Setup checklist:</p>
                  <div className="space-y-1.5">
                    {["GA4 property created and tag installed site-wide (via Google Tag Manager)", "Verify firing on every page: homepage, menu, ordering, specials, about, location", "No duplicate tags (common issue that inflates data)", "Enhanced measurement enabled (scroll depth, outbound clicks, site search)", "Cross-domain tracking if ordering is on a subdomain or third-party URL"].map((item, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700"><CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Custom events I'd track:</p>
                  <div className="space-y-1.5">
                    {[
                      { event: "online_order_started", why: "How many people click 'Order Online'" },
                      { event: "online_order_completed", why: "Conversion rate from visit to order" },
                      { event: "menu_item_viewed", why: "Which items get the most attention" },
                      { event: "loyalty_signup", why: "Track QR code → loyalty conversion" },
                      { event: "directions_clicked", why: "Intent signal for dine-in" },
                      { event: "phone_call_clicked", why: "Track call-to-action taps on mobile" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 font-mono flex-shrink-0">{item.event}</code>
                        <span className="text-gray-500">{item.why}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>Why I lead with this:</strong> I've seen businesses spend $500/mo on Google Ads while their site has broken indexing, no analytics, and zero schema. The ads drive clicks to a page Google barely understands, with no tracking to measure results. Fix the foundation first — then every dollar spent on ads, content, and social works harder because the infrastructure actually captures and converts that traffic.
            </p>
          </div>

          {/* On-Page SEO */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">On-Page SEO — Every Page Needs to Earn Its Rank</h3>
            <p className="text-gray-600 text-xs leading-relaxed mb-3">
              Search engines read your pages from top to bottom. If the title tag is generic, the meta description is missing, and the images have no alt text — you're handing rankings to competitors who bothered to fill them in.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">What I'd audit and fix:</p>
                <div className="space-y-1.5">
                  {[
                    "Unique title tag on every page — keyword-first, under 60 characters",
                    "Meta description on every page — compelling, includes CTA, under 155 chars",
                    "One H1 per page with the primary keyword (e.g., \"Order Comfort Food Online — Lily's Silver Spoon\")",
                    "Alt text on every image — descriptive, keyword-relevant (not \"IMG_3847.jpg\")",
                    "Long-tail keywords woven naturally into body copy and subheadings",
                    "Internal linking between related pages (menu → ordering, specials → loyalty)",
                    "Open Graph tags for social sharing (image, title, description)",
                    "Canonical tags to prevent duplicate content issues",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700"><CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Example — Menu Page:</p>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-xs">
                  <div><span className="font-bold text-gray-500">Title:</span> <span className="text-gray-700">Menu — Burgers, Comfort Food & Brunch | Lily's Silver Spoon</span></div>
                  <div><span className="font-bold text-gray-500">Meta:</span> <span className="text-gray-700">Explore our full menu — from our famous Classic Cheeseburger to Buttermilk Pancakes. Order online for pickup or delivery. No app needed.</span></div>
                  <div><span className="font-bold text-gray-500">H1:</span> <span className="text-gray-700">Our Menu — Comfort Food Made From Scratch Since 1995</span></div>
                  <div><span className="font-bold text-gray-500">Alt text (burger image):</span> <span className="text-gray-700">"Classic cheeseburger with lettuce, tomato, and house sauce at Lily's Silver Spoon"</span></div>
                </div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mt-3 mb-2">Long-tail keywords to target:</p>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-xs text-gray-700">
                  <p>"best comfort food near [college name]"</p>
                  <p>"restaurant with mac and cheese near me"</p>
                  <p>"order brunch online [city]"</p>
                  <p>"affordable dinner spot [city]"</p>
                  <p>"where to get buttermilk pancakes near me"</p>
                  <p>"family restaurant with outdoor seating [city]"</p>
                </div>
              </div>
            </div>
          </div>

          {/* Maps & Directions */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Google Maps & Apple Maps — Make It Dead Simple to Find You</h3>
            <p className="text-gray-600 text-xs leading-relaxed mb-3">
              70% of diners search on mobile. If they can't tap a button and get turn-by-turn directions in 2 seconds, you've lost them. Both map platforms need to be covered — Google dominates Android, Apple dominates iPhone.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Google Maps Integration</p>
                <div className="space-y-1">
                  {[
                    "Embedded Google Map on the location/contact page",
                    "\"Get Directions\" button that opens Google Maps app directly",
                    "GeoCoordinates in schema markup so Google pins the exact location",
                    "Google Maps link in footer of every page",
                    "Google Maps link in every email, every ad sitelink",
                  ].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><CheckCircle size={8} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</p>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Apple Maps Integration</p>
                <div className="space-y-1">
                  {[
                    "Apple Maps \"Open in Maps\" link for iPhone users (maps:// protocol)",
                    "Claim and optimize Apple Business Connect listing",
                    "Ensure hours, photos, and menu match Google listing",
                    "Smart direction button: detects iOS vs Android, opens correct app",
                    "Apple Maps shows in Siri results — listing must be accurate",
                  ].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><CheckCircle size={8} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile-First */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Mobile-First Design — Because That's How College Kids Order</h3>
            <p className="text-gray-600 text-xs leading-relaxed mb-3">
              4,000 students nearby. They're not opening a laptop to order food — they're on their phone at 9pm deciding what to eat in 30 seconds. The entire site has to be built mobile-first, not desktop-first with a responsive afterthought.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Speed</p>
                <div className="space-y-1">
                  {["Under 3 seconds to fully load on 4G", "Compressed images (WebP format, lazy loaded)", "Minimal JavaScript — no bloated frameworks", "Core Web Vitals passing (LCP, FID, CLS)", "CDN for fast delivery regardless of location"].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Layout</p>
                <div className="space-y-1">
                  {["Thumb-friendly tap targets (min 44px)", "Sticky \"Order Now\" button on mobile", "Click-to-call phone number", "Menu readable without pinch-zooming", "Cart/ordering is one-thumb operable"].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Student UX</p>
                <div className="space-y-1">
                  {["\"Student Deals\" prominent on mobile homepage", "Apple Pay / Google Pay for one-tap checkout", "Loyalty signup is 2 taps (QR → phone number)", "Instagram link in header (where students live)", "Dark mode friendly (they're ordering at night)"].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-gray-700 text-xs"><strong>Google indexes mobile-first.</strong> Since 2021, Google primarily uses the mobile version of your site for indexing and ranking. If your mobile experience is slow, broken, or missing content compared to desktop — your rankings suffer on <em>all</em> devices, not just phones.</p>
            </div>
          </div>

        </div>
      </section>

      {/* ═══ GEO-TARGETED LANDING PAGES ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <MapPin className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Geo-Targeted City Landing Pages — Pull in Nearby Patrons</h2>
              <p className="text-gray-500 text-sm mt-1">One page per city. Unique content. Rank locally in every neighborhood within driving distance.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            When someone in a nearby city searches "restaurants near me" or "best burger in [city]," Lily's won't show up unless there's a page that specifically mentions that city with relevant, unique content. A single location page isn't enough — you need a page for every city within your delivery and dine-in radius.
          </p>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Cities I'd Build Pages For</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { city: "Home City", type: "Primary", highlight: true },
                { city: "Downtown District", type: "Primary", highlight: true },
                { city: "College Campus Area", type: "Primary", highlight: true },
                { city: "Nearby Suburb 1", type: "Secondary", highlight: false },
                { city: "Nearby Suburb 2", type: "Secondary", highlight: false },
                { city: "Nearby Suburb 3", type: "Secondary", highlight: false },
                { city: "Adjacent City 1", type: "Tertiary", highlight: false },
                { city: "Adjacent City 2", type: "Tertiary", highlight: false },
              ].map((c, i) => (
                <div key={i} className={`rounded-lg p-2.5 text-center ${c.highlight ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-100'}`}>
                  <p className="text-xs font-semibold text-gray-800">{c.city}</p>
                  <p className={`text-[10px] ${c.highlight ? 'text-amber-600' : 'text-gray-400'}`}>{c.type}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-3">Each page gets its own URL: <code className="bg-gray-100 px-1 rounded text-[10px]">/comfort-food-[city-name]</code></p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">What Each City Page Needs (Not Template-Cloned)</h3>
            <p className="text-gray-600 text-xs mb-3">Google penalizes thin, duplicate pages with just the city name swapped in. Each page needs genuinely unique content:</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                {[
                  "City-specific H1: \"Best Comfort Food Near [City] — Lily's Silver Spoon\"",
                  "Opening paragraph about the city and its food scene",
                  "Driving directions + drive time from that city to Lily's",
                  "Embedded Google Map with directions pre-filled from that city",
                  "Mention of local landmarks, neighborhoods, or college campuses",
                  "City-specific customer testimonials if available",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700"><CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</div>
                ))}
              </div>
              <div className="space-y-1.5">
                {[
                  "\"Popular orders from [City] customers\" — highlights high-margin items",
                  "Delivery availability and estimated time from that area",
                  "Pickup instructions specific to coming from that direction",
                  "LocalBusiness + GeoCoordinates schema on every page",
                  "Unique meta title and description targeting [City] + food keywords",
                  "Internal link to online ordering page with a strong CTA",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700"><CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Sample City Page — What It Should Read Like</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-bold text-gray-800 mb-1">/comfort-food-downtown</p>
              <p className="text-gray-600 text-xs italic leading-relaxed">
                "Working late downtown? Studying at the library until your eyes cross? We get it. Lily's Silver Spoon is a 7-minute drive from the downtown square — close enough for a quick lunch, easy enough for dinner takeout on the way home. Our Mac & Cheese is the go-to for downtown regulars who want real food, not another sad desk lunch. Order online for pickup and we'll have it ready when you walk in."
              </p>
            </div>
            <p className="text-gray-500 text-xs mt-2">Unique. Local. Conversational. Targets "food near downtown" and "takeout downtown [city]" naturally. Google rewards this kind of content over a template that just says "We serve [City]."</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-gray-800 text-xs"><strong>Impact:</strong> 8 city pages = 8 new Google-indexed pages targeting local searches. Pair with Spanish versions (/es/comfort-food-[city]) and you've got 16 geo-targeted pages — each one a net for nearby customers searching for exactly what Lily's offers.</p>
          </div>
        </div>
      </section>

      {/* ═══ FAQ SECTION ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Search className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">FAQ Page — Capture Every Long-Tail Search</h2>
              <p className="text-gray-500 text-sm mt-1">Every question a customer asks is a keyword opportunity. With FAQPage schema, these answers show up directly in Google results and voice search.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            People don't just search "restaurant near me." They ask specific questions — and Google increasingly shows direct answers pulled from FAQ pages. Every question below is a real search query with local intent. With FAQPage schema markup, Google can pull Lily's answer directly into the search results as a featured snippet.
          </p>

          <div className="space-y-3 mb-6">
            {[
              {
                q: "How much does a burger cost at Lily's Silver Spoon?",
                a: "Our Classic Cheeseburger is $15, made with a hand-formed patty, house sauce, and locally baked brioche bun. It's our most popular item — and for good reason.",
                keywords: "burger price, how much, lily's silver spoon menu"
              },
              {
                q: "Does Lily's Silver Spoon offer online ordering?",
                a: "Yes! You can order directly from our website — no DoorDash or UberEats needed. Order for pickup or delivery with no app fees. First order is 10% off with code ORDERDIRECT.",
                keywords: "online ordering, delivery, order food, pickup"
              },
              {
                q: "Is Lily's Silver Spoon good for students?",
                a: "Absolutely. We're 5 minutes from campus, offer 15% off with student ID for dine-in, and have free WiFi. Our Mac & Cheese and Buttermilk Pancakes are student favorites.",
                keywords: "student discount, near college, cheap eats, student friendly"
              },
              {
                q: "What are Lily's Silver Spoon's hours?",
                a: "We're open Monday through Sunday, 7am to 10pm. Breakfast is served until 2pm, and our full dinner menu runs 5pm to close. Walk-ins are always welcome.",
                keywords: "hours, open now, when open, breakfast hours"
              },
              {
                q: "Does Lily's have a rewards or loyalty program?",
                a: "Yes — scan the QR code at your table to join. No app download needed. Enter your phone number and start earning points immediately. Your first reward: a free Chocolate Lava Cake on your next visit.",
                keywords: "loyalty program, rewards, free food, loyalty card"
              },
              {
                q: "Can I order Lily's for a large group or catering?",
                a: "Yes, we offer catering and group dining packages. Our Roasted Half Chicken, Steak Frites, and Mac & Cheese are popular for group orders. Call us or order online for groups of 10+.",
                keywords: "catering, large group, party, event dining"
              },
              {
                q: "What kind of food does Lily's Silver Spoon serve?",
                a: "We're a seasonal farm-to-table bistro serving American comfort food — think hand-formed burgers, mac & cheese from scratch, buttermilk pancakes, grilled salmon, and steak frites. Everything's sourced locally and made fresh. We've been here since 1995.",
                keywords: "what kind of food, menu, comfort food, farm to table, american restaurant"
              },
              {
                q: "Is there parking at Lily's Silver Spoon?",
                a: "Yes, we have a free parking lot with 30+ spaces right in front of the restaurant. Street parking is also available on both sides.",
                keywords: "parking, free parking, where to park"
              },
              {
                q: "Does Lily's Silver Spoon have outdoor seating?",
                a: "Yes, we have a patio with 8 tables that's open weather permitting. It's dog-friendly too.",
                keywords: "outdoor seating, patio, dog friendly, outside dining"
              },
              {
                q: "What's the best thing to order at Lily's Silver Spoon?",
                a: "Our regulars swear by the Classic Cheeseburger and the Mac & Cheese. For brunch, the Buttermilk Pancakes are legendary. And save room for the Chocolate Lava Cake — it's worth it.",
                keywords: "best dish, what to order, popular items, must try"
              },
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="font-bold text-gray-900 text-sm mb-1">{faq.q}</p>
                <p className="text-gray-600 text-xs leading-relaxed mb-2">{faq.a}</p>
                <p className="text-[10px] text-gray-400">Target keywords: <span className="text-gray-500">{faq.keywords}</span></p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Why FAQPage Schema Changes the Game</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Without schema:</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-blue-700 text-sm font-medium">Lily's Silver Spoon</p>
                  <p className="text-green-700 text-[10px]">lilyssilverspoon.com</p>
                  <p className="text-gray-600 text-xs">Neighborhood bistro serving comfort food since 1995...</p>
                </div>
                <p className="text-gray-400 text-[10px] mt-1">Standard result — easy to scroll past</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">With FAQPage schema:</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-blue-700 text-sm font-medium">Lily's Silver Spoon</p>
                  <p className="text-green-700 text-[10px]">lilyssilverspoon.com</p>
                  <p className="text-gray-600 text-xs">Neighborhood bistro serving comfort food since 1995...</p>
                  <div className="mt-2 border-t border-gray-200 pt-2 space-y-1">
                    <p className="text-xs text-gray-800 font-medium">Does Lily's offer online ordering? &#9660;</p>
                    <p className="text-xs text-gray-800 font-medium">Is Lily's good for students? &#9660;</p>
                    <p className="text-xs text-gray-800 font-medium">What are Lily's hours? &#9660;</p>
                  </div>
                </div>
                <p className="text-green-600 text-[10px] mt-1 font-medium">Rich result — takes up 3x the space, higher click-through rate</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-gray-800 text-xs"><strong>Voice search bonus:</strong> When someone asks Siri "Does Lily's Silver Spoon have outdoor seating?" or Google Assistant "What's the best thing to order at Lily's?" — the FAQ answer is exactly what gets read aloud. FAQPage schema is how you become the voice search answer.</p>
          </div>
        </div>
      </section>

      {/* ═══ GOOGLE ADS CAMPAIGN STRUCTURE ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Target className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Sample Google Ads Campaign — How I'd Structure It</h2>
              <p className="text-gray-500 text-sm mt-1">Three campaigns targeting Lily's biggest opportunities.</p>
            </div>
          </div>

          {/* Campaign Structure */}
          <div className="space-y-4 mb-8">
            {/* Campaign 1 */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-blue-600 px-5 py-3">
                <p className="text-white font-bold text-sm">Campaign 1: Direct Online Ordering</p>
                <p className="text-blue-200 text-xs">Goal: Recapture orders from DoorDash/UberEats | Budget: $15/day | Geo: 8-mile radius</p>
              </div>
              <div className="p-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Ad Group 1: Brand + Ordering</p>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <p className="text-xs text-gray-500 font-medium">Keywords:</p>
                      <p className="text-xs text-gray-700">"lily's silver spoon order online"</p>
                      <p className="text-xs text-gray-700">"lily's silver spoon delivery"</p>
                      <p className="text-xs text-gray-700">"lily's silver spoon menu"</p>
                      <p className="text-xs text-gray-700">"lily's restaurant order food"</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Ad Group 2: Non-Brand + Ordering</p>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <p className="text-xs text-gray-500 font-medium">Keywords:</p>
                      <p className="text-xs text-gray-700">"order food online near me"</p>
                      <p className="text-xs text-gray-700">"comfort food delivery [city]"</p>
                      <p className="text-xs text-gray-700">"best burger delivery near me"</p>
                      <p className="text-xs text-gray-700">"restaurant takeout [city]"</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Campaign 2 */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-green-600 px-5 py-3">
                <p className="text-white font-bold text-sm">Campaign 2: Dine-In — Slow Daypart Fill</p>
                <p className="text-green-200 text-xs">Goal: Fill Monday/Wednesday dinner + Saturday breakfast | Budget: $10/day | Ad Schedule: Mon/Wed 2-5pm, Sat 6-9am</p>
              </div>
              <div className="p-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Ad Group 1: Monday Specials</p>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <p className="text-xs text-gray-500 font-medium">Keywords:</p>
                      <p className="text-xs text-gray-700">"monday dinner deals near me"</p>
                      <p className="text-xs text-gray-700">"restaurant specials tonight"</p>
                      <p className="text-xs text-gray-700">"burger and beer deal [city]"</p>
                      <p className="text-xs text-gray-700">"where to eat tonight [city]"</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Ad Group 2: Weekend Brunch</p>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <p className="text-xs text-gray-500 font-medium">Keywords:</p>
                      <p className="text-xs text-gray-700">"brunch near me"</p>
                      <p className="text-xs text-gray-700">"saturday breakfast [city]"</p>
                      <p className="text-xs text-gray-700">"best pancakes near me"</p>
                      <p className="text-xs text-gray-700">"weekend brunch spots"</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Campaign 3 */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-purple-600 px-5 py-3">
                <p className="text-white font-bold text-sm">Campaign 3: Student Acquisition</p>
                <p className="text-purple-200 text-xs">Goal: Drive college traffic + loyalty signups | Budget: $8/day | Audience: 18-24, 3-mile radius of campus</p>
              </div>
              <div className="p-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Ad Group 1: Student Deals</p>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <p className="text-xs text-gray-500 font-medium">Keywords:</p>
                      <p className="text-xs text-gray-700">"cheap eats near [college]"</p>
                      <p className="text-xs text-gray-700">"student food deals [city]"</p>
                      <p className="text-xs text-gray-700">"late night food near campus"</p>
                      <p className="text-xs text-gray-700">"study spots with food [city]"</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Ad Group 2: Group Dining</p>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <p className="text-xs text-gray-500 font-medium">Keywords:</p>
                      <p className="text-xs text-gray-700">"restaurants for groups near me"</p>
                      <p className="text-xs text-gray-700">"birthday dinner [city]"</p>
                      <p className="text-xs text-gray-700">"good restaurants near [college]"</p>
                      <p className="text-xs text-gray-700">"date night restaurant [city]"</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SEARCH AD MOCKUPS ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Search className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Google Search Ad Mockups</h2>
              <p className="text-gray-500 text-sm mt-1">What these ads look like in search results.</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Search Ad 1 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-400 mb-2">Campaign 1 — Direct Ordering</p>
              <div className="border-l-2 border-blue-500 pl-4">
                <p className="text-xs text-green-700 mb-0.5">Ad &middot; lilyssilversspoon.com/order</p>
                <p className="text-blue-700 text-base font-medium mb-0.5 hover:underline cursor-pointer">Order Lily's Silver Spoon Online — Skip the App, Save More</p>
                <p className="text-gray-600 text-xs leading-relaxed">Order direct from Lily's — no delivery app markups. Fresh comfort food, ready in 20 min. Mac & Cheese, Burgers, Salmon & more. First online order: 10% off with code ORDERDIRECT.</p>
                <div className="flex gap-4 mt-1.5">
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">View Full Menu</span>
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">Order Now</span>
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">Catering Available</span>
                </div>
              </div>
            </div>

            {/* Search Ad 2 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-400 mb-2">Campaign 2 — Monday Night Fill</p>
              <div className="border-l-2 border-green-500 pl-4">
                <p className="text-xs text-green-700 mb-0.5">Ad &middot; lilyssilverspoon.com/specials</p>
                <p className="text-blue-700 text-base font-medium mb-0.5 hover:underline cursor-pointer">Monday Night at Lily's — Burger & a Beer for $16</p>
                <p className="text-gray-600 text-xs leading-relaxed">Skip cooking Monday. Our Classic Cheeseburger + draft beer, $16 every Monday 5-10pm. Dine-in only. Walk-ins welcome. 25 years of comfort food done right.</p>
                <div className="flex gap-4 mt-1.5">
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">See Monday Menu</span>
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">Get Directions</span>
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">Call (555) 123-4567</span>
                </div>
              </div>
            </div>

            {/* Search Ad 3 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-400 mb-2">Campaign 3 — Student Acquisition</p>
              <div className="border-l-2 border-purple-500 pl-4">
                <p className="text-xs text-green-700 mb-0.5">Ad &middot; lilyssilverspoon.com/students</p>
                <p className="text-blue-700 text-base font-medium mb-0.5 hover:underline cursor-pointer">Lily's Silver Spoon — Student Menu Starting at $9.99</p>
                <p className="text-gray-600 text-xs leading-relaxed">Real food, not dining hall food. Show your student ID for 15% off dine-in. Mac & Cheese, Buttermilk Pancakes, Classic Burgers. 5 min from campus. Free WiFi.</p>
                <div className="flex gap-4 mt-1.5">
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">Student Deals</span>
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">Order Pickup</span>
                  <span className="text-blue-600 text-xs hover:underline cursor-pointer">Join Rewards</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ GEO-FENCING STRATEGY ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <MapPin className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Geo-Fencing — Reach Students Where They Are, When They're Hungry</h2>
              <p className="text-gray-500 text-sm mt-1">Draw a digital perimeter around high-value locations and serve ads to every phone inside it.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Geo-fencing lets you target mobile ads to people physically inside a defined area — the campus, competing restaurants, local event venues, even the parking lot of a grocery store at dinnertime. When someone steps inside the fence, they become eligible to see your ad. For a restaurant with 4,000 students a few blocks away, this is one of the highest-ROI tactics available.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Where to Draw the Fences</h3>
              <div className="space-y-2">
                {[
                  { location: "College campus", why: "4,000 students deciding where to eat — every day", priority: true },
                  { location: "Competing restaurants", why: "Conquest targeting — show your deal while they wait for a table", priority: true },
                  { location: "Student housing / dorms", why: "Dinner decision happens at home between 5-7pm", priority: true },
                  { location: "Campus library & study halls", why: "Late-night study sessions = takeout orders", priority: false },
                  { location: "Local event venues", why: "Game days, concerts, move-in weekend = hungry crowds", priority: false },
                  { location: "Nearby grocery stores", why: "Catch the 'I don't feel like cooking' moment", priority: false },
                ].map((item, i) => (
                  <div key={i} className={`flex items-start gap-2 text-xs ${item.priority ? 'text-gray-800' : 'text-gray-600'}`}>
                    <MapPin size={10} className={`mt-0.5 flex-shrink-0 ${item.priority ? 'text-amber-600' : 'text-gray-400'}`} />
                    <div>
                      <span className={item.priority ? 'font-semibold' : ''}>{item.location}</span>
                      <span className="text-gray-500"> — {item.why}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-3">When to Trigger Ads</h3>
              <div className="space-y-3">
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-amber-800 mb-1">Monday 3-6pm — Campus Fence</p>
                  <p className="text-xs text-gray-700">"Monday Night at Lily's — Burger & a Beer, $16. Show your student ID." Triggers right when students are deciding on dinner plans.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-green-800 mb-1">Saturday 7-10am — Dorm / Housing Fence</p>
                  <p className="text-xs text-gray-700">"Pancake Recovery — pancakes + iced latte, one price. Order for pickup." Catches the weekend brunch decision before they commit elsewhere.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-700 mb-1">Daily 11am-1pm — Campus + Library</p>
                  <p className="text-xs text-gray-600">"Skip the dining hall. Order Lily's — ready in 15 min." Lunch window with direct ordering link.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">How It Works with Toast</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">1. Set the Fence</p>
                <p className="text-[10px] text-gray-600">Draw a polygon around the campus, dorms, or competing restaurants. Radius as tight as 100 meters — or as wide as a 2-mile zone.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">2. Serve the Ad</p>
                <p className="text-[10px] text-gray-600">When someone's phone enters the fence during your scheduled hours, they see a mobile ad in apps and mobile web. Direct link to Toast Online Ordering.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">3. Measure Foot Traffic</p>
                <p className="text-[10px] text-gray-600">Track how many people who saw the ad later visited Lily's (via location data). Cross-reference with Toast check-in times and order volume.</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Budget & Expected ROI</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">$5-10</p>
                <p className="text-[10px] text-gray-500">Daily budget</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">$150-300</p>
                <p className="text-[10px] text-gray-500">Monthly spend</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-amber-600">5,000-15,000</p>
                <p className="text-[10px] text-gray-500">Monthly impressions</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-green-700">$3-5</p>
                <p className="text-[10px] text-gray-500">Cost per visit</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>Why geo-fencing is a no-brainer for Lily's:</strong> Most restaurant marketing is broad — you're paying to reach people 20 miles away who will never visit. Geo-fencing flips that. You only pay to reach people who are <em>already nearby</em> and <em>already deciding where to eat</em>. At $5-10/day, even converting 2-3 extra orders daily pays for itself immediately. Pair it with the "ORDERDIRECT" promo code and you're pulling those orders onto Toast's first-party platform instead of DoorDash — recovering margin and building loyalty at the same time.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ RETARGETING PIXEL & DRIP CAMPAIGNS ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Eye className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Tracking Pixel + Drip Campaigns — Turn Browsers Into Regulars</h2>
              <p className="text-gray-500 text-sm mt-1">Every website visitor who doesn't call, click, or order is a warm lead walking away. A pixel changes that.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Right now, if someone visits Lily's website, looks at the menu, and leaves without ordering — that's it. They're gone. No follow-up, no reminder, nothing. A tracking pixel (Meta Pixel, Google Tag, or a unified solution like a "super pixel") captures every one of those visitors anonymously and builds a retargeting audience you can reach for weeks afterward. Pair that with automated drip campaigns and you turn one-time browsers into repeat customers.
          </p>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">What the Pixel Captures</h3>
            <p className="text-gray-600 text-xs leading-relaxed mb-3">
              Every visitor action becomes a signal. The pixel fires silently on every page and tracks behavior without requiring any signup or personal info from the visitor.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Page-Level Signals</p>
                <div className="space-y-1.5">
                  {[
                    "Visited the menu page but didn't order",
                    "Looked at the online ordering page but abandoned",
                    "Viewed the specials/promotions page",
                    "Checked hours or location (high dine-in intent)",
                    "Visited 3+ pages in one session (engaged browser)",
                    "Returned visitor who still hasn't converted",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                      <CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Conversion Events to Track</p>
                <div className="space-y-1.5">
                  {[
                    { event: "menu_viewed", desc: "Looked at the menu" },
                    { event: "order_started", desc: "Clicked 'Order Online'" },
                    { event: "order_completed", desc: "Finished a purchase" },
                    { event: "directions_clicked", desc: "Tapped 'Get Directions'" },
                    { event: "phone_clicked", desc: "Tapped the phone number" },
                    { event: "loyalty_signup", desc: "Scanned QR / joined rewards" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 font-mono flex-shrink-0">{item.event}</code>
                      <span className="text-gray-500">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Automated Drip Campaigns — The Follow-Up Machine</h3>
            <p className="text-gray-600 text-xs leading-relaxed mb-4">
              Once the pixel builds your audience, automated campaigns do the work of following up — without you lifting a finger. Each audience segment gets a tailored sequence based on what they did (or didn't do) on the site.
            </p>

            <div className="space-y-4">
              {/* Drip 1: Menu Browsers */}
              <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
                <div className="bg-red-50 px-4 py-2.5 border-b border-red-200">
                  <p className="text-red-800 font-bold text-xs">Drip 1: Menu Browsers Who Didn't Order</p>
                  <p className="text-red-600 text-[10px]">Trigger: Viewed menu page but no order_started event within 24 hours</p>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    {[
                      { day: "Day 1", channel: "Retargeting Ad", msg: "\"Still thinking about that Cheeseburger? Order direct — 10% off with code ORDERDIRECT.\" → Link to Toast ordering page with the burger pre-highlighted." },
                      { day: "Day 3", channel: "Retargeting Ad", msg: "\"Your Mac & Cheese is waiting. Skip the app fees — order from Lily's directly.\" → Rotating creative featuring high-margin items." },
                      { day: "Day 7", channel: "Retargeting Ad", msg: "\"Lily's Silver Spoon — comfort food made from scratch since 1995. See what's on tonight.\" → Broader brand ad with the storefront image." },
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs">
                        <span className="text-red-600 font-bold flex-shrink-0 w-10">{step.day}</span>
                        <span className="text-gray-500 flex-shrink-0 w-24">{step.channel}</span>
                        <span className="text-gray-700">{step.msg}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Drip 2: Order Abandoners */}
              <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
                <div className="bg-amber-50 px-4 py-2.5 border-b border-amber-200">
                  <p className="text-amber-800 font-bold text-xs">Drip 2: Cart / Order Abandoners</p>
                  <p className="text-amber-600 text-[10px]">Trigger: order_started but no order_completed within 1 hour</p>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    {[
                      { day: "1 hour", channel: "Retargeting Ad", msg: "\"You left something delicious behind. Your order is still waiting — finish it now and save 10%.\" → Direct link back to Toast ordering." },
                      { day: "Day 1", channel: "Retargeting Ad", msg: "\"Hungry again? Pick up where you left off. Lily's — ready in 15 minutes.\" → Personalized based on last items viewed." },
                      { day: "Day 4", channel: "Retargeting Ad", msg: "\"Join Lily's rewards — earn free food on every order. Your first reward: a free Chocolate Lava Cake.\" → Loyalty signup push." },
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs">
                        <span className="text-amber-600 font-bold flex-shrink-0 w-10">{step.day}</span>
                        <span className="text-gray-500 flex-shrink-0 w-24">{step.channel}</span>
                        <span className="text-gray-700">{step.msg}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Drip 3: High-Intent Visitors */}
              <div className="bg-white rounded-xl border border-green-200 overflow-hidden">
                <div className="bg-green-50 px-4 py-2.5 border-b border-green-200">
                  <p className="text-green-800 font-bold text-xs">Drip 3: High-Intent Visitors (Directions / Phone / 3+ Pages)</p>
                  <p className="text-green-600 text-[10px]">Trigger: directions_clicked or phone_clicked or 3+ page views — but no order</p>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    {[
                      { day: "Day 1", channel: "Retargeting Ad", msg: "\"We saw you looking — come say hi. Monday Night: Burger & a Beer, $16. Student ID gets you 15% off dine-in.\" → Dine-in focused creative." },
                      { day: "Day 3", channel: "Retargeting Ad", msg: "\"Lily's Silver Spoon — 5 minutes from campus, free parking, walk-ins welcome. See tonight's specials.\" → Location and convenience focused." },
                      { day: "Day 7", channel: "Retargeting Ad", msg: "\"Join 663 regulars who love Lily's. Scan the QR at your table to start earning free food.\" → Social proof + loyalty." },
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs">
                        <span className="text-green-600 font-bold flex-shrink-0 w-10">{step.day}</span>
                        <span className="text-gray-500 flex-shrink-0 w-24">{step.channel}</span>
                        <span className="text-gray-700">{step.msg}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Pixel Setup — What Gets Installed</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Meta Pixel (Facebook/Instagram)</p>
                <div className="space-y-1">
                  {[
                    "Install via Google Tag Manager (one tag, fires site-wide)",
                    "Tracks all page views + custom conversion events",
                    "Builds Custom Audiences for retargeting on Facebook & Instagram",
                    "Lookalike Audiences: find new people who look like your best customers",
                    "Required for the display retargeting ads in this plan",
                  ].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Google Ads Tag</p>
                <div className="space-y-1">
                  {[
                    "Global site tag + conversion tracking via GTM",
                    "Tracks conversions from Google Search and Display campaigns",
                    "Builds remarketing lists for Google Display Network",
                    "Enables the retargeting display ads (cart abandonment, awareness)",
                    "Cross-device tracking — follows users from phone to desktop",
                  ].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">Unified "Super Pixel" Option</p>
                <div className="space-y-1">
                  {[
                    "Services like CustomerLabs, Segmetrics, or HighLevel combine all pixels into one",
                    "Single install manages Meta, Google, TikTok, and email platform tracking",
                    "Server-side tracking bypasses ad blockers (captures 20-30% more data)",
                    "Auto-syncs audiences across all ad platforms",
                    "Ideal if running multi-platform campaigns from day one",
                  ].map((item, i) => (
                    <p key={i} className="text-[10px] text-gray-600 flex items-start gap-1"><span className="text-gray-400">&#8250;</span>{item}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Budget & Expected Impact</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">$0</p>
                <p className="text-[10px] text-gray-500">Pixel setup cost</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">$5-15/day</p>
                <p className="text-[10px] text-gray-500">Retargeting ad spend</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-amber-600">3-5x</p>
                <p className="text-[10px] text-gray-500">Higher conversion vs cold ads</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-green-700">30 days</p>
                <p className="text-[10px] text-gray-500">Follow-up window per visitor</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>Why this matters for Lily's:</strong> Retargeting ads convert 3-5x better than cold ads because you're reaching people who already showed interest. Right now, every person who visits Lily's website and leaves is a lost opportunity — no pixel means no second chance. The pixel costs nothing to install. The retargeting campaigns cost $5-15/day. And the drip sequences run automatically — once set up, they follow up with every single visitor without any manual effort. Combined with geo-fencing, this creates a closed loop: geo-fencing brings new people to the site, the pixel captures them, and the drip campaigns bring them back until they convert.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ DISPLAY AD MOCKUPS ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Eye className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Google Display Ad Concepts</h2>
              <p className="text-gray-500 text-sm mt-1">Retargeting and awareness banners for the Google Display Network.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Display Ad 1 - Retargeting */}
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="bg-amber-800 p-6 text-center relative">
                <p className="text-amber-200 text-[10px] uppercase tracking-widest mb-2">You left something delicious behind</p>
                <p className="text-white text-xl font-bold leading-tight mb-1">Finish Your Order</p>
                <p className="text-white text-xl font-bold leading-tight mb-3">at Lily's</p>
                <p className="text-amber-200 text-xs mb-4">Your Mac & Cheese is waiting. Order direct — no app fees.</p>
                <div className="inline-block bg-white text-amber-800 font-bold text-xs px-5 py-2 rounded-full">ORDER NOW — 10% OFF</div>
              </div>
              <div className="bg-white p-3 flex items-center justify-between">
                <p className="text-[10px] text-gray-400">300x250 — Retargeting (cart abandonment)</p>
                <p className="text-[10px] text-gray-400">GDN</p>
              </div>
            </div>

            {/* Display Ad 2 - Awareness */}
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="bg-gray-900 p-6 text-center relative">
                <p className="text-gray-400 text-[10px] uppercase tracking-widest mb-2">Lily's Silver Spoon</p>
                <p className="text-white text-xl font-bold leading-tight mb-1">Monday Night</p>
                <p className="text-amber-400 text-xl font-bold leading-tight mb-3">Burger & Beer $16</p>
                <p className="text-gray-400 text-xs mb-4">Every Monday 5-10pm. Walk-ins welcome. 25 years of comfort food.</p>
                <div className="inline-block bg-amber-500 text-white font-bold text-xs px-5 py-2 rounded-full">GET DIRECTIONS</div>
              </div>
              <div className="bg-white p-3 flex items-center justify-between">
                <p className="text-[10px] text-gray-400">300x250 — Daypart promo (Monday geo-targeted)</p>
                <p className="text-[10px] text-gray-400">GDN</p>
              </div>
            </div>

            {/* Display Ad 3 - Student */}
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="bg-purple-900 p-6 text-center relative">
                <p className="text-purple-300 text-[10px] uppercase tracking-widest mb-2">For students who eat real food</p>
                <p className="text-white text-xl font-bold leading-tight mb-1">15% Off with</p>
                <p className="text-purple-300 text-xl font-bold leading-tight mb-3">Student ID</p>
                <p className="text-purple-200 text-xs mb-4">Buttermilk Pancakes. Classic Burgers. Mac & Cheese. Free WiFi.</p>
                <div className="inline-block bg-white text-purple-800 font-bold text-xs px-5 py-2 rounded-full">SEE STUDENT MENU</div>
              </div>
              <div className="bg-white p-3 flex items-center justify-between">
                <p className="text-[10px] text-gray-400">300x250 — Student acquisition (campus geo-fence)</p>
                <p className="text-[10px] text-gray-400">GDN</p>
              </div>
            </div>

            {/* Display Ad 4 - Loyalty */}
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-br from-amber-600 to-orange-700 p-6 text-center relative">
                <p className="text-amber-200 text-[10px] uppercase tracking-widest mb-2">Lily's Rewards</p>
                <p className="text-white text-xl font-bold leading-tight mb-1">Free Chocolate</p>
                <p className="text-white text-xl font-bold leading-tight mb-3">Lava Cake</p>
                <p className="text-amber-100 text-xs mb-4">Join our loyalty program. Scan the QR at your table. First reward on us.</p>
                <div className="inline-block bg-white text-amber-700 font-bold text-xs px-5 py-2 rounded-full">JOIN FREE REWARDS</div>
              </div>
              <div className="bg-white p-3 flex items-center justify-between">
                <p className="text-[10px] text-gray-400">300x250 — Loyalty acquisition (retargeting past diners)</p>
                <p className="text-[10px] text-gray-400">GDN</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL MEDIA POSTS ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Users className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Instagram, TikTok & Facebook Post Concepts</h2>
              <p className="text-gray-500 text-sm mt-1">Platform-specific content that leans into Lily's real identity: seasonal farm-to-table comfort food made from scratch.</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Post 1 - IG Reel */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-gradient-to-b from-pink-500 via-purple-500 to-amber-500 flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-amber-500 rounded-lg" />
                      <span className="text-xs font-bold text-gray-700">Instagram Reel</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Monday 11am</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">15-sec Reel: "Farm to fork in 24 hours"</p>
                  <p className="text-gray-600 text-xs leading-relaxed mb-2">Open on seasonal produce arriving at the kitchen door. Quick cuts: herbs being chopped, steak hitting the cast iron, plating the Steak Frites. Text overlay: "From a local farm to your plate — that's how we've done it since 1995." End card: "Order direct — link in bio."</p>
                  <p className="text-gray-500 text-xs italic">Caption: "Our Steak Frites starts at the farm, not the freezer. Seasonal ingredients, made from scratch, served the way it should be. Come taste the difference. Link in bio to skip the apps."</p>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#FarmToTable</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#SeasonalMenu</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#SupportLocal</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#LilysSilverSpoon</span>
                  </div>
                </div>
              </div>
            </div>

            {/* TikTok 1 - Kitchen POV */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-black flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-black rounded-lg flex items-center justify-center"><span className="text-white text-[8px] font-bold">TT</span></div>
                      <span className="text-xs font-bold text-gray-700">TikTok</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Monday 7pm</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">"POV: You're the chef at a farm-to-table bistro next to a college campus"</p>
                  <p className="text-gray-600 text-xs leading-relaxed mb-2">Trending audio. Start with unboxing seasonal produce delivery. Quick cuts: washing herbs, breaking down a chicken, the Mac & Cheese pull shot, plating the burger. Final shot: handing a bag to a college student at the pickup window. Text overlay: "We've been doing this since 1995. Skip the apps — order direct."</p>
                  <p className="text-gray-500 text-xs italic">Strategy: POV format gets 2-3x reach on TikTok vs standard food videos. Farm-to-table angle hits the "authentic/local" algorithm trend. College proximity creates local relevance for the FYP.</p>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#FarmToFork</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#RestaurantLife</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#CollegeEats</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#POV</span>
                  </div>
                </div>
              </div>
            </div>

            {/* TikTok 2 - Seasonal Menu Drop */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-black flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-black rounded-lg flex items-center justify-center"><span className="text-white text-[8px] font-bold">TT</span></div>
                      <span className="text-xs font-bold text-gray-700">TikTok</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">1st of each month</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">"New seasonal menu just dropped" — Monthly menu reveal</p>
                  <p className="text-gray-600 text-xs leading-relaxed mb-2">ASMR-style. Close-up of each new seasonal dish being plated. No talking — just kitchen sounds, sizzle, steam, the pour. Text overlays naming each dish and the local farm it came from. End with: "Available now. Order direct — link in bio."</p>
                  <p className="text-gray-500 text-xs italic">Strategy: "Menu drop" format mirrors fashion/sneaker drops Gen Z loves. Monthly cadence creates anticipation. ASMR food content consistently trends on TikTok. Tagging local farms drives cross-audience reach.</p>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#SeasonalMenu</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#ASMR</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#NewMenuAlert</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#FoodTok</span>
                  </div>
                </div>
              </div>
            </div>

            {/* TikTok 3 - Student challenge */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-black flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-black rounded-lg flex items-center justify-center"><span className="text-white text-[8px] font-bold">TT</span></div>
                      <span className="text-xs font-bold text-gray-700">TikTok</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Wednesdays 5pm</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">"Rate our Mac & Cheese 1-10" — Student taste test series</p>
                  <p className="text-gray-600 text-xs leading-relaxed mb-2">Invite a different student each week to try a signature dish on camera. Genuine reaction, no script. They rate it, then reveal the farm-to-table ingredient story behind the dish. End card: "Come try it yourself — student ID = 15% off dine-in."</p>
                  <p className="text-gray-500 text-xs italic">Strategy: Taste test format is one of TikTok's highest-engagement food formats. Using real students creates UGC that feels organic. Tagging the student creates cross-pollination with their followers. Farm-to-table story adds depth beyond "yummy food."</p>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#TasteTest</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#CollegeFood</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#HonestReview</span>
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">#FarmFresh</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Post 2 - FB */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-blue-600 flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-600 rounded-lg" />
                      <span className="text-xs font-bold text-gray-700">Facebook Post</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Tuesday 6pm</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">Photo: Wine + Steak Frites, warm lighting</p>
                  <p className="text-gray-500 text-xs italic leading-relaxed">"Tomorrow is Wine Down Wednesday. Half-price bottles with any entree, 5-10pm. No reservation needed. Just walk in, sit down, and let us take care of the rest. We've been doing this for 25 years — we know what a good Wednesday night looks like. 🍷"</p>
                  <div className="mt-2 bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-800">Lily's Silver Spoon</p>
                      <p className="text-[10px] text-gray-400">Wine Down Wednesday — Half-Price Bottles</p>
                    </div>
                    <span className="text-[10px] bg-blue-600 text-white px-3 py-1 rounded font-medium">Learn More</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Post 3 - IG Story */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-gradient-to-b from-pink-500 via-purple-500 to-amber-500 flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-amber-500 rounded-lg" />
                      <span className="text-xs font-bold text-gray-700">Instagram Story (Series of 3)</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Thursday 4pm</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">"Did you know?" — Loyalty QR code education</p>
                  <div className="text-gray-600 text-xs leading-relaxed space-y-1.5">
                    <p><strong>Story 1:</strong> "Did you know there's a QR code at your table?" — photo of the tabletop tent card</p>
                    <p><strong>Story 2:</strong> "Scan it. Enter your number. That's it." — screen recording of the 10-second signup</p>
                    <p><strong>Story 3:</strong> "Your first reward? A free Chocolate Lava Cake on your next visit." — close-up of the cake with a "Swipe Up to Order" CTA</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Post 4 - IG Carousel */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-gradient-to-b from-pink-500 via-purple-500 to-amber-500 flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-amber-500 rounded-lg" />
                      <span className="text-xs font-bold text-gray-700">Instagram Carousel (5 slides)</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Saturday 10am</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">"5 Things You Didn't Know About Lily's Silver Spoon"</p>
                  <div className="text-gray-600 text-xs leading-relaxed space-y-1">
                    <p><strong>Slide 1:</strong> "We've been here since 1995" — Chef Lily Henderson founding story</p>
                    <p><strong>Slide 2:</strong> "Every dish starts at a local farm" — photo of seasonal produce delivery with farm name</p>
                    <p><strong>Slide 3:</strong> "Our menu changes with the seasons" — side-by-side of summer vs winter specials</p>
                    <p><strong>Slide 4:</strong> "You can order direct from us — no DoorDash needed" — phone screen showing ordering page</p>
                    <p><strong>Slide 5:</strong> "Students get 15% off with ID" — table of college students laughing, eating</p>
                  </div>
                  <p className="text-gray-500 text-xs italic mt-2">Caption: "25 years. Seasonal ingredients. Made from scratch every day. Which one surprised you? 👇"</p>
                </div>
              </div>
            </div>

            {/* Post 5 - FB Event */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex">
                <div className="w-2 bg-blue-600 flex-shrink-0" />
                <div className="p-5 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-600 rounded-lg" />
                      <span className="text-xs font-bold text-gray-700">Facebook Event</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Recurring Weekly</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm mb-1">"Weekend Brunch at Lily's" — Saturday & Sunday recurring event</p>
                  <p className="text-gray-500 text-xs italic leading-relaxed">"Buttermilk Pancakes. Iced Lattes. The Saturday morning you deserve. Every weekend, 7am-2pm. Walk-ins always welcome. Bring the family, bring your friends, bring your appetite."</p>
                  <div className="mt-2 bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500"><strong>Strategy note:</strong> FB Events get free recurring visibility in local feeds. Creating a weekly recurring event for brunch builds habitual awareness and drives Saturday breakfast traffic (currently weakest AM at $1,100).</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Calendar Summary */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-800 text-sm mb-3">Weekly Content Calendar Overview</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 text-gray-500 font-medium">Day</th>
                    <th className="text-left py-2 px-2 text-gray-500 font-medium">Platform</th>
                    <th className="text-left py-2 px-2 text-gray-500 font-medium">Content</th>
                    <th className="text-left py-2 px-2 text-gray-500 font-medium">Goal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { day: "Monday", platform: "IG Reel + TikTok", content: "Farm-to-table food hero (steak sourcing, burger sizzle)", goal: "Engagement + direct ordering" },
                    { day: "Tuesday", platform: "FB Post", content: "Wine Down Wednesday promo (seasonal pairing)", goal: "Fill Wed dinner" },
                    { day: "Wednesday", platform: "TikTok + IG Story", content: "Student taste test + behind-the-scenes kitchen", goal: "UGC + brand personality" },
                    { day: "Thursday", platform: "IG Story (series)", content: "Loyalty QR education / seasonal ingredient spotlight", goal: "Loyalty signups" },
                    { day: "Friday", platform: "IG + FB + TikTok", content: "Weekend brunch teaser (pancake stack, latte art)", goal: "Fill Saturday breakfast" },
                    { day: "Saturday", platform: "IG Carousel", content: "\"Did you know\" — farm-to-table brand story", goal: "Reach + saves" },
                    { day: "Sunday", platform: "FB + TikTok", content: "Customer spotlight / farmer feature / review share", goal: "Social proof + reviews" },
                    { day: "1st of Month", platform: "TikTok + IG Reel", content: "\"New seasonal menu just dropped\" — ASMR reveal", goal: "Anticipation + virality" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 px-2 font-medium text-gray-800">{row.day}</td>
                      <td className="py-2 px-2 text-gray-600">{row.platform}</td>
                      <td className="py-2 px-2 text-gray-600">{row.content}</td>
                      <td className="py-2 px-2 text-gray-500">{row.goal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ COLLEGE INFLUENCER PROGRAM ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Star className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">College Influencer Program — Turn Students Into Your Marketing Team</h2>
              <p className="text-gray-500 text-sm mt-1">4,000 students nearby. Some of them already have audiences. Let them sell for you.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            You don't need a celebrity endorsement. A college student with 2,000 Instagram followers who posts a genuine photo of your Cheeseburger reaches more potential customers in your trade area than a $500 ad buy. Their followers are their classmates, their friends, their roommates — the exact people you want walking through the door.
          </p>

          {/* The Program */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-4">Lily's Campus Creator Program</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100 text-center">
                <p className="text-2xl font-bold text-amber-600 mb-1">Free Meal</p>
                <p className="text-xs text-gray-600">Come in, eat on us. Post about it honestly. That's it.</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100 text-center">
                <p className="text-2xl font-bold text-amber-600 mb-1">$0 Cost</p>
                <p className="text-xs text-gray-600">One meal (~$20 food cost) for reach that beats paid ads</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100 text-center">
                <p className="text-2xl font-bold text-amber-600 mb-1">Authentic</p>
                <p className="text-xs text-gray-600">Not scripted. Real students, real food, real opinions.</p>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">How It Works</h3>
            <div className="space-y-3">
              {[
                {
                  step: "1",
                  title: "Identify 10-15 campus micro-influencers",
                  desc: "Look for students with 1K-10K followers on Instagram or TikTok who post about food, campus life, or local spots. They don't need to be huge — they need to be trusted by their peers. Check the school's food-related accounts, foodie hashtags, and student orgs."
                },
                {
                  step: "2",
                  title: "Invite them to a \"Creator Night\" at Lily's",
                  desc: "One evening per month, 5-8 creators come in. Free meal from the menu — appetizer, entree, dessert. They eat, they photograph, they film. No script, no requirements beyond tagging @lilyssilverspoon and using a branded hashtag (#EatAtLilys)."
                },
                {
                  step: "3",
                  title: "Give each creator a personal promo code",
                  desc: "\"SARAH10\" gives their followers 10% off online orders. This does two things: (1) drives direct orders instead of DoorDash, and (2) lets you track exactly which creator drives the most revenue. The data tells you who to invite back."
                },
                {
                  step: "4",
                  title: "Repost their content on Lily's channels",
                  desc: "UGC (user-generated content) performs 4x better than brand-created content because it looks real — because it is real. Repost their Reels, Stories, and TikToks to Lily's account with credit. Free content that's more authentic than anything you'd produce in-house."
                },
                {
                  step: "5",
                  title: "Promote top-performing creators with a small ad spend",
                  desc: "Take the best-performing creator post and put $20-50 behind it as a paid Instagram or TikTok ad, geo-targeted to the campus area. A creator's video promoted as a sponsored post converts significantly better than a brand ad — because it doesn't look like an ad."
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{item.step}</div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                    <p className="text-gray-600 text-xs mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Economics */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">The Economics — Why This Is the Best ROI in the Plan</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 text-gray-500 font-medium">Metric</th>
                    <th className="text-left py-2 px-2 text-gray-500 font-medium">Google Ads</th>
                    <th className="text-left py-2 px-2 text-gray-500 font-medium">Creator Night</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { metric: "Cost per month", ads: "$300-500 ad spend", creator: "~$120 food cost (6 creators x ~$20)" },
                    { metric: "Reach", ads: "~5,000-10,000 impressions", creator: "~10,000-50,000 (combined follower reach)" },
                    { metric: "Trust factor", ads: "Low — it's an ad", creator: "High — it's their friend's recommendation" },
                    { metric: "Content produced", ads: "0 — you write the ad copy", creator: "6-12 Reels/Stories/TikToks (free UGC)" },
                    { metric: "Tracking", ads: "Click-through rate", creator: "Promo codes track exact revenue per creator" },
                    { metric: "Longevity", ads: "Stops when budget stops", creator: "Content lives forever on their profiles" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 px-2 font-medium text-gray-800">{row.metric}</td>
                      <td className="py-2 px-2 text-gray-600">{row.ads}</td>
                      <td className="py-2 px-2 text-gray-700 font-medium">{row.creator}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Content Ideas */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Content Prompts for Creators</h3>
            <p className="text-gray-500 text-xs mb-3">Give them ideas, not scripts. Authenticity dies the moment you hand someone a script.</p>
            <div className="grid md:grid-cols-2 gap-2">
              {[
                "\"First time at Lily's — here's what I ordered\" (classic first-visit Reel)",
                "\"I tried ordering direct instead of DoorDash — here's the difference\"",
                "\"Studying at Lily's — free WiFi, real food, and this Mac & Cheese\"",
                "\"Date night for under $50\" (Steak Frites + wine + Lava Cake)",
                "\"Monday night burger deal — $16 for this\" (close-up reveal)",
                "\"My ranking of everything I've eaten at Lily's\" (tier list format)",
                "\"Behind the scenes — the chef let me watch them make pancakes\"",
                "\"Bringing my friends here for the first time\" (reaction video)",
              ].map((idea, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                  <span className="text-amber-400 text-xs mt-0.5">&#9679;</span>
                  <span className="text-gray-700 text-xs italic">{idea}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>The bigger picture:</strong> This isn't just about one month of free posts. When 10 students with campus influence all post about Lily's in the same week, it creates a perception shift — suddenly Lily's is "the spot." That kind of cultural momentum doesn't come from ad spend. It comes from real people saying real things. And once it starts, their followers tag their friends, those friends visit, and the cycle feeds itself. The total cost? Six plates of food and one evening of your time.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ PROGRESSIVE WEB APP (PWA) ═══ */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Target className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Progressive Web App — Lily's on Every Home Screen, No App Store Needed</h2>
              <p className="text-gray-500 text-sm mt-1">Turn the website into an installable app that customers open like DoorDash — but it's Lily's own channel.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            A Progressive Web App (PWA) lets customers "install" Lily's website directly from their browser onto their phone's home screen. No App Store, no Google Play, no download, no updates. It looks and behaves like a native app — full screen, custom icon, fast loading, even works offline for the menu. And here's why it matters: an icon on the home screen is a daily reminder to order direct instead of opening DoorDash.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* What customers see */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">What the Customer Sees</p>

              {/* Phone mockup */}
              <div className="bg-gray-900 rounded-2xl p-3 mx-auto max-w-[200px] mb-4">
                <div className="bg-white rounded-xl p-3">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      { name: "DoorDash", color: "#FF3008", text: "DD" },
                      { name: "Uber Eats", color: "#06C167", text: "UE" },
                      { name: "Lily's", color: "#C07A3A", text: "LS", highlight: true },
                      { name: "Instagram", color: "#E1306C", text: "IG" },
                    ].map((app, i) => (
                      <div key={i} className="text-center">
                        <div className={`w-10 h-10 rounded-xl mx-auto mb-1 flex items-center justify-center text-white text-[8px] font-bold ${app.highlight ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                          style={{ background: app.color }}>
                          {app.text}
                        </div>
                        <p className="text-[7px] text-gray-600">{app.name}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                    <p className="text-[8px] text-amber-800 font-bold">Lily's lives right next to DoorDash</p>
                    <p className="text-[7px] text-amber-600">Same home screen. No commission.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  "Customer visits lilyssilverspoon.com on their phone",
                  "Browser shows \"Add to Home Screen\" prompt",
                  "One tap — Lily's icon appears next to DoorDash",
                  "Opens full-screen (no browser bar) to the ordering page",
                  "Loads instantly, even on slow connections",
                  "Push notifications for specials (with opt-in)",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                    <span className="w-4 h-4 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0">{i + 1}</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Why it matters */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-3">Why This Changes the Ordering Behavior</p>

              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 font-bold text-xs mb-1">The core problem</p>
                  <p className="text-green-700 text-xs leading-relaxed">When a customer is hungry, they open whatever app is on their home screen. Right now that's DoorDash (30% commission). If Lily's is on the home screen too, you're competing on equal footing — and winning, because direct ordering is cheaper for the customer.</p>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-800">PWA vs Native App vs Website:</p>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-2 py-1.5 font-semibold text-gray-700"></th>
                          <th className="text-center px-2 py-1.5 font-semibold text-amber-700">PWA</th>
                          <th className="text-center px-2 py-1.5 font-semibold text-gray-500">Native App</th>
                          <th className="text-center px-2 py-1.5 font-semibold text-gray-500">Website</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { feature: "Home screen icon", pwa: "Yes", native: "Yes", web: "No" },
                          { feature: "Full-screen experience", pwa: "Yes", native: "Yes", web: "No" },
                          { feature: "Works offline (menu)", pwa: "Yes", native: "Yes", web: "No" },
                          { feature: "Push notifications", pwa: "Yes", native: "Yes", web: "No" },
                          { feature: "App Store required", pwa: "No", native: "Yes", web: "No" },
                          { feature: "Development cost", pwa: "$0-500", native: "$15K-50K", web: "$0" },
                          { feature: "Update instantly", pwa: "Yes", native: "Review wait", web: "Yes" },
                          { feature: "Install friction", pwa: "1 tap", native: "Store + download", web: "N/A" },
                        ].map((row, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-2 py-1.5 text-gray-700 font-medium">{row.feature}</td>
                            <td className="px-2 py-1.5 text-center text-amber-700 font-semibold">{row.pwa}</td>
                            <td className="px-2 py-1.5 text-center text-gray-500">{row.native}</td>
                            <td className="px-2 py-1.5 text-center text-gray-500">{row.web}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-800 mb-1.5">Implementation cost: nearly zero</p>
                  <p className="text-[10px] text-gray-600 leading-relaxed">A PWA requires adding a <code className="bg-gray-200 px-1 rounded text-[9px]">manifest.json</code> file (icon, name, colors) and a <code className="bg-gray-200 px-1 rounded text-[9px]">service-worker.js</code> (caching). That's it. If the website is already built, converting it to a PWA takes an afternoon. Toast's ordering page can be the default "app" start page.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Integration with the rest of the plan */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">How PWA Fits Into the Full Strategy</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">+ QR Tabletop</p>
                <p className="text-[10px] text-gray-600">The QR code at each table can prompt "Add Lily's to your home screen" — every dine-in customer becomes a direct-ordering channel for life.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">+ Loyalty Program</p>
                <p className="text-[10px] text-gray-600">When customers open the PWA, they see their loyalty points balance first. That's a daily engagement touchpoint DoorDash can't offer.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-800 mb-1.5">+ Push Notifications</p>
                <p className="text-[10px] text-gray-600">"Monday Night Special: Burger & Beer, $16" — sent directly to their phone at 4pm. No email open rate to worry about, no social algorithm to fight.</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>The bottom line:</strong> A PWA puts Lily's ordering app on every customer's home screen — right next to DoorDash, Uber Eats, and Instagram — for nearly zero cost. No App Store approval, no $50K development budget, no download friction. Just a website that behaves like an app. Every time a customer reaches for their phone to order food, Lily's is one tap away on their own channel with zero commission. That's the endgame for first-party ordering recovery.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ MAP STACKING ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <MapPin className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Google Map Stacking — Multiply Your Local Search Signals</h2>
              <p className="text-gray-500 text-sm mt-1">One Google Maps listing is a pin. A stack of maps is a fortress.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Map stacking means creating multiple Google My Maps properties — each targeting a different keyword theme — all pointing back to Lily's. Each map gets embedded on the matching page of your website. Google sees multiple geo-verified signals reinforcing that Lily's is THE result for those terms in that area. It's one of the most underused local SEO tactics, and it's free.
          </p>

          {/* How It Works */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">How Map Stacking Works</h3>
            <div className="space-y-3">
              {[
                { step: "1", title: "Create themed Google My Maps", desc: "Each map targets a specific keyword cluster. Not one map for everything — separate maps for separate search intents." },
                { step: "2", title: "Pin Lily's + nearby landmarks", desc: "Each map pins Lily's location alongside landmarks the target audience knows (campus buildings, downtown spots, neighborhoods). This gives Google geographic context." },
                { step: "3", title: "Add keyword-rich descriptions to each pin", desc: "The map title, description, and pin labels all contain target keywords. Google indexes My Maps content — it's free SEO real estate." },
                { step: "4", title: "Embed each map on the matching website page", desc: "The \"Student Dining\" map embeds on the /students page. The \"Brunch\" map embeds on the brunch page. Each city map embeds on its city landing page. This creates a loop: website → map → Google → website." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-7 h-7 bg-gray-800 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{item.step}</div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                    <p className="text-gray-600 text-xs mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* The Stack */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Lily's Map Stack — 9 Properties</h3>
            <div className="space-y-2">
              {[
                {
                  map: "Best Comfort Food Near [College]",
                  pins: "Lily's + campus library, student center, dorms",
                  embed: "/students",
                  keywords: "food near campus, student restaurants, cheap eats near [college]"
                },
                {
                  map: "Brunch Restaurants [City]",
                  pins: "Lily's + popular Saturday morning spots in the area",
                  embed: "/brunch",
                  keywords: "brunch near me, saturday breakfast, best pancakes [city]"
                },
                {
                  map: "Restaurants with Online Ordering [City]",
                  pins: "Lily's as primary, links to direct ordering page",
                  embed: "/order",
                  keywords: "order food online [city], restaurant delivery, takeout near me"
                },
                {
                  map: "Date Night Restaurants [City]",
                  pins: "Lily's + nearby entertainment, theaters, parks",
                  embed: "/dinner",
                  keywords: "date night restaurant, romantic dinner [city], nice restaurant near me"
                },
                {
                  map: "Best Burgers [City]",
                  pins: "Lily's highlighted among burger spots in the area",
                  embed: "/menu (burger section)",
                  keywords: "best burger near me, cheeseburger [city], burger restaurant"
                },
                {
                  map: "Comfort Food Delivery [City]",
                  pins: "Lily's with delivery radius overlay",
                  embed: "/delivery",
                  keywords: "comfort food delivery, mac and cheese delivery, food delivery [city]"
                },
                {
                  map: "Restaurants Near [Suburb 1]",
                  pins: "Lily's + route from suburb with drive time noted",
                  embed: "/comfort-food-[suburb1]",
                  keywords: "restaurants near [suburb], where to eat [suburb], dinner [suburb]"
                },
                {
                  map: "Restaurants Near [Suburb 2]",
                  pins: "Lily's + route from suburb with drive time noted",
                  embed: "/comfort-food-[suburb2]",
                  keywords: "restaurants near [suburb2], food near [suburb2]"
                },
                {
                  map: "Catering & Group Dining [City]",
                  pins: "Lily's + nearby event venues, offices, campus event spaces",
                  embed: "/catering",
                  keywords: "catering near me, group dining [city], restaurant for parties"
                },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] bg-gray-800 text-white px-1.5 py-0.5 rounded font-mono">{i + 1}</span>
                        <p className="font-semibold text-gray-900 text-xs">{item.map}</p>
                      </div>
                      <p className="text-[10px] text-gray-500">Pins: {item.pins}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-gray-400">Embeds on</p>
                      <p className="text-[10px] font-mono text-gray-600">{item.embed}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Keywords: <span className="text-gray-500">{item.keywords}</span></p>
                </div>
              ))}
            </div>
          </div>

          {/* Why It Compounds */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Why Map Stacking Compounds With Everything Else</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-2">
                {[
                  { combo: "Maps + City Landing Pages", result: "Each geo page has an embedded map with directions from that city — double the local signal" },
                  { combo: "Maps + Schema Markup", result: "GeoCoordinates schema on the page + Google My Map embed = Google cross-verifies your location from two sources" },
                  { combo: "Maps + Spanish Pages", result: "Spanish city pages get Spanish-described map embeds — local signals in both languages" },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-bold text-gray-800">{item.combo}</p>
                    <p className="text-[10px] text-gray-600 mt-0.5">{item.result}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {[
                  { combo: "Maps + Google Ads", result: "Location extensions in ads pull from verified map data — your ad shows a map pin" },
                  { combo: "Maps + FAQ Content", result: "\"Where is Lily's located?\" FAQ + embedded map = featured snippet with a map" },
                  { combo: "Maps + Influencer Content", result: "Creators tag the location — Google Maps sees engagement signals on the pin" },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-bold text-gray-800">{item.combo}</p>
                    <p className="text-[10px] text-gray-600 mt-0.5">{item.result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>Cost: $0. Time: 2-3 hours to build all 9 maps.</strong> Each map is a permanent Google-indexed asset that reinforces Lily's location authority. Combined with schema, city pages, and the llms.txt file, this creates a local SEO moat that competitors would need months to replicate. Most restaurants have one Google listing and nothing else — Lily's would have 9 map properties, 8+ city pages, FAQPage schema, and AI-ready structured data. That's how you own local search.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ AI SEARCH OPTIMIZATION ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Target className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">AI Search Optimization — Get Recommended by Gemini, ChatGPT, Perplexity & Siri</h2>
              <p className="text-gray-500 text-sm mt-1">Google isn't the only search engine anymore. AI assistants are now recommending restaurants directly.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            When a college student asks ChatGPT "best burger near campus" or says "Hey Siri, where should I eat tonight" — the AI pulls from structured web data to form its answer. If Lily's isn't in that data, it's invisible to the fastest-growing search channel. This is a new layer of SEO that most restaurants haven't even heard of yet.
          </p>

          {/* llms.txt */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">llms.txt — A Curated Reading List for AI</h3>
            <p className="text-gray-600 text-xs leading-relaxed mb-3">
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">llms.txt</code> is a new standard — a Markdown file at your site's root that tells AI models which pages matter most and what your business is about. Think of it as a sitemap specifically for AI, not search engine crawlers.
            </p>
            <div className="bg-gray-900 rounded-lg p-4 mb-3 overflow-x-auto">
              <pre className="text-green-400 text-xs leading-relaxed font-mono whitespace-pre">{`# Lily's Silver Spoon

> Neighborhood bistro serving American comfort food since 1995.
> Located near [College], open 7 days a week, 7am-10pm.
> Known for our Classic Cheeseburger, Mac & Cheese, and
> Buttermilk Pancakes. Order direct at lilyssilverspoon.com/order.

## Menu & Ordering
- [Full Menu](https://lilyssilverspoon.com/menu): All dishes with prices and descriptions
- [Order Online](https://lilyssilverspoon.com/order): Direct ordering, no third-party apps
- [Catering](https://lilyssilverspoon.com/catering): Group and event orders

## About
- [Our Story](https://lilyssilverspoon.com/about): Founded by Chef Lily Henderson in 1995
- [Location & Hours](https://lilyssilverspoon.com/location): Directions, parking, hours

## Specials & Deals
- [Student Deals](https://lilyssilverspoon.com/students): 15% off with student ID
- [Monday Burger Night](https://lilyssilverspoon.com/specials): Burger & beer $16 every Monday
- [Wine Down Wednesday](https://lilyssilverspoon.com/specials): Half-price wine bottles
- [Loyalty Rewards](https://lilyssilverspoon.com/rewards): Scan QR at your table to join`}</pre>
            </div>
            <p className="text-gray-500 text-xs">This file lives at <code className="bg-gray-100 px-1 rounded text-[10px]">lilyssilverspoon.com/llms.txt</code> — any AI crawling the site finds it instantly.</p>
          </div>

          {/* Full AI Search Strategy */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Full AI Search Visibility Checklist</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Structured Data for AI</p>
                <div className="space-y-1.5">
                  {[
                    "llms.txt file at site root with curated page map",
                    "FAQPage schema — AI pulls Q&A answers directly",
                    "Restaurant + LocalBusiness schema — AI reads your hours, cuisine, price range",
                    "Menu schema with items, prices, descriptions — AI can answer \"how much is the burger?\"",
                    "AggregateRating schema — AI cites your review score in recommendations",
                    "GeoCoordinates — AI knows exactly where you are for \"near me\" queries",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700"><CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Content That AI Cites</p>
                <div className="space-y-1.5">
                  {[
                    "Conversational FAQ content matching how people ask AI questions",
                    "Long-form blog posts with clear, factual answers (not fluff)",
                    "\"Best of\" and comparison content (\"best comfort food near [city]\")",
                    "Specific pricing on the menu page (AI needs concrete data to recommend)",
                    "Clear, non-generic meta descriptions (AI reads these for context)",
                    "Unique city landing pages (AI localizes recommendations by area)",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs text-gray-700"><CheckCircle size={10} className="text-green-500 mt-0.5 flex-shrink-0" />{item}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* AI Platforms */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Where AI Recommendations Happen</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: "Google Gemini", desc: "AI Overviews in Google Search — the answer box above all results" },
                { name: "ChatGPT", desc: "130M+ weekly users asking for restaurant recs" },
                { name: "Perplexity", desc: "AI search engine that cites sources — your site needs to be citeable" },
                { name: "Siri / Apple Intelligence", desc: "\"Where should I eat?\" pulls from Apple Maps + web data" },
              ].map((platform, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs font-bold text-gray-800 mb-1">{platform.name}</p>
                  <p className="text-[10px] text-gray-500">{platform.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Example */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-2">What an AI Recommendation Looks Like</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-2">User asks: "What's a good comfort food restaurant near [College]?"</p>
              <div className="border-l-2 border-blue-400 pl-3">
                <p className="text-gray-800 text-sm leading-relaxed">
                  <strong>Lily's Silver Spoon</strong> is a neighborhood bistro about 5 minutes from campus, open since 1995. Known for their Classic Cheeseburger ($15), homemade Mac & Cheese, and Buttermilk Pancakes. They offer 15% off with student ID for dine-in, and you can order directly from their website without DoorDash fees. They also have a loyalty program — scan the QR code at your table for a free Chocolate Lava Cake on your next visit. Open 7am-10pm daily.
                </p>
              </div>
              <p className="text-green-600 text-[10px] mt-2 font-medium">This answer is only possible when your site has structured data, clear pricing, FAQ content, and an llms.txt file that maps it all together.</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>Why this is the future:</strong> 40%+ of Gen Z already uses TikTok and AI chatbots as their primary search tool — not Google. That number grows every semester. The restaurants that show up in AI recommendations today will own the habit loop for the next decade. Setting up llms.txt, proper schema, and FAQ content takes a few hours. The competitive advantage lasts years.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ COMPETITOR AUDIT CARD ═══ */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <BarChart3 className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Competitor Audit — Lily's vs. the Local Landscape</h2>
              <p className="text-gray-500 text-sm mt-1">A side-by-side look at what customers see when they search "restaurants near me."</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            Customers don't evaluate Lily's in a vacuum — they compare it to every other option on the screen. Here's how Lily's Google Business Profile stacks up against the three closest competitors. The gaps are fixable, but right now they're costing real traffic.
          </p>

          {/* Comparison Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-2 sm:px-4 py-2.5 font-semibold text-gray-700 text-[10px] sm:text-xs">Metric</th>
                  <th className="text-center px-2 sm:px-4 py-2.5 font-semibold text-red-700 bg-red-50 text-[10px] sm:text-xs">Lily's</th>
                  <th className="text-center px-2 sm:px-4 py-2.5 font-semibold text-gray-700 text-[10px] sm:text-xs">Comp A</th>
                  <th className="text-center px-2 sm:px-4 py-2.5 font-semibold text-gray-700 text-[10px] sm:text-xs">Comp B</th>
                  <th className="text-center px-2 sm:px-4 py-2.5 font-semibold text-gray-700 text-[10px] sm:text-xs">Comp C</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { metric: "Google Rating", lilys: "3.8★", a: "4.4★", b: "4.2★", c: "4.5★", bad: true },
                  { metric: "Total Reviews", lilys: "23", a: "267", b: "189", c: "312", bad: true },
                  { metric: "Price Category", lilys: "$$$ (wrong)", a: "$$", b: "$$", c: "$$", bad: true },
                  { metric: "Category Label", lilys: "Fine Dining", a: "American", b: "Comfort Food", c: "Bistro", bad: true },
                  { metric: "Photos", lilys: "7", a: "45+", b: "30+", c: "60+", bad: true },
                  { metric: "Hours Listed", lilys: "Missing", a: "Yes", b: "Yes", c: "Yes", bad: true },
                  { metric: "Phone Listed", lilys: "Missing", a: "Yes", b: "Yes", c: "Yes", bad: true },
                  { metric: "Website Link", lilys: "Missing", a: "Yes", b: "Yes", c: "Yes", bad: true },
                  { metric: "Menu Available", lilys: "No", a: "Yes", b: "Yes", c: "Yes", bad: true },
                  { metric: "Google Posts", lilys: "None", a: "Weekly", b: "Monthly", c: "Weekly", bad: true },
                  { metric: "Order Link", lilys: "Buried", a: "Prominent", b: "Prominent", c: "DoorDash", bad: true },
                  { metric: "Avg Entree", lilys: "$21", a: "$18", b: "$22", c: "$19", bad: false },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-2 sm:px-4 py-2 text-gray-800 font-medium text-[10px] sm:text-xs">{row.metric}</td>
                    <td className={`px-2 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-semibold ${row.bad ? 'text-red-600 bg-red-50/50' : 'text-gray-700'}`}>{row.lilys}</td>
                    <td className="px-2 sm:px-4 py-2 text-center text-[10px] sm:text-xs text-gray-600">{row.a}</td>
                    <td className="px-2 sm:px-4 py-2 text-center text-[10px] sm:text-xs text-gray-600">{row.b}</td>
                    <td className="px-2 sm:px-4 py-2 text-center text-[10px] sm:text-xs text-gray-600">{row.c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-red-200 p-4">
              <p className="text-red-700 font-bold text-sm mb-2">Reviews Gap</p>
              <p className="text-3xl font-bold text-red-600 mb-1">23 vs 256</p>
              <p className="text-xs text-gray-600">Lily's has 23 reviews. The average competitor has 256. That's an 11x gap. At 3.8 stars, Lily's also has the lowest rating — most customers won't consider a restaurant below 4.0.</p>
            </div>
            <div className="bg-white rounded-xl border border-amber-200 p-4">
              <p className="text-amber-700 font-bold text-sm mb-2">Visibility Gap</p>
              <p className="text-3xl font-bold text-amber-600 mb-1">7 vs 45+</p>
              <p className="text-xs text-gray-600">Competitors have 4-8x more photos and complete profile info (hours, phone, menu, website). Lily's listing is essentially a blank page with the wrong label on it.</p>
            </div>
            <div className="bg-white rounded-xl border border-green-200 p-4">
              <p className="text-green-700 font-bold text-sm mb-2">The Opportunity</p>
              <p className="text-3xl font-bold text-green-600 mb-1">90 days</p>
              <p className="text-xs text-gray-600">None of this is permanent. A photo blitz, profile cleanup, and review campaign can close these gaps in 90 days. The food is already good — the listing just needs to show it.</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>What this means:</strong> Right now, when someone searches "restaurants near me," they see three competitors with 4.2-4.5 stars, hundreds of reviews, full photo galleries, and complete info — and then they see Lily's with 3.8 stars, 23 reviews, "Fine Dining $$$", and missing basics. The decision is made in 3 seconds, and Lily's loses before the customer even tastes the food. Fix the listing, launch the review campaign, and this table looks completely different in 90 days.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ ROI CALCULATOR ═══ */}
      <ROICalculator />

      {/* ═══ MY TAKE — HUMAN STRATEGY + AI EXECUTION ═══ */}
      <section className="py-12 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-6">
            <Star className="text-gray-900 mt-1" size={20} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">A note on how I work — and what came from where</h2>
              <p className="text-gray-500 text-sm mt-1">You asked for authentic strategic thinking and a human point of view. Here's the breakdown.</p>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            I use AI as a production tool — the way a designer uses Photoshop or a strategist uses a spreadsheet. It helps me build faster, but it doesn't think for me. Every strategic insight in this plan came from me sitting with the data, looking at the restaurant's real online presence, and thinking about what I'd actually recommend if I were hired tomorrow. Here's what that looks like in practice.
          </p>

          {/* My Strategic Calls */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-amber-500"></div>
              <h3 className="font-bold text-gray-900 text-sm">My strategic observations — the things I noticed that AI didn't</h3>
            </div>
            <p className="text-gray-600 text-xs mb-4">These are the ideas, connections, and calls I made by reading the case study data, Googling the restaurant, and thinking like a marketer — not prompting a chatbot.</p>

            <div className="space-y-3">
              {[
                {
                  insight: "Dollar margins beat percentage margins — and that changes everything",
                  detail: "The case study gives margin percentages. Most people would push the 80% Iced Latte. I ran the math: the 58% Steak Frites puts $13.92 in your pocket vs the Latte's $4.40. That flips the entire promotion strategy. Every upsell, every \"Chef's Pick,\" every featured item should be weighted by dollar margin. I caught this because I've seen restaurants optimize for the wrong metric before.",
                  color: "amber"
                },
                {
                  insight: "\"Seasonal farm-to-table\" is the brand — and it's completely invisible online",
                  detail: "I noticed the restaurant's concept mentions seasonal farm-to-table ingredients. That's a genuine differentiator in a market where competitors are generic \"American restaurants.\" But the listing, website, and all online presence say nothing about it. I called this out because it changes the content strategy: seasonal menu drops, farmer spotlights, ingredient stories — that's infinite organic content most restaurants can't replicate.",
                  color: "green"
                },
                {
                  insight: "Where's the TikTok strategy? 4,000 students and no TikTok presence?",
                  detail: "The initial plan had Instagram and Facebook but zero TikTok. With a college campus next door, that's like building a billboard and facing it away from the highway. I flagged this and built out three TikTok-specific formats: POV kitchen content, monthly \"menu drop\" reveals (mirrors fashion/sneaker drop culture), and student taste test series. These formats are native to how Gen Z discovers restaurants.",
                  color: "pink"
                },
                {
                  insight: "Geo-fencing is the obvious play nobody mentioned",
                  detail: "With 4,000 students within walking distance, drawing a digital fence around the campus, dorms, and competing restaurants at meal-decision hours is one of the highest-ROI tactics available. $5-10/day to reach phones that are physically inside your trade area, right when they're deciding where to eat. I asked the question because I've seen this work for other local businesses.",
                  color: "blue"
                },
                {
                  insight: "A super-pixel + drip campaigns turns every website visitor into a follow-up",
                  detail: "I asked: \"What about adding a super-pixel to go after visitors who never clicked or called?\" Right now, someone browses the menu and leaves — gone forever. A tracking pixel captures them, and automated drip sequences follow up for 30 days based on what they did on the site. Menu browsers get one sequence, cart abandoners get another, high-intent visitors get a third. This is standard in e-commerce but almost no restaurants do it.",
                  color: "purple"
                },
                {
                  insight: "The competitor audit tells the story the data alone can't",
                  detail: "Numbers in isolation don't sell. Showing 3.8 stars and 23 reviews next to three competitors averaging 4.4 stars and 256 reviews — that's a visual gut-punch that makes the urgency undeniable. I pushed for a side-by-side comparison table because it makes the case more effectively than any paragraph could.",
                  color: "red"
                },
                {
                  insight: "SEO, Search Console, Bing IndexNow, and schema are table stakes nobody's doing",
                  detail: "I went deeper on the technical foundation because I've seen businesses spend money on ads while their site has broken indexing, no analytics, and zero structured data. Schema markup, Google Search Console, Bing IndexNow for instant indexing, map stacking with 9 Google My Maps — these are all free and compound over time. Most agencies skip this because it's not sexy, but it's where the long-term traffic comes from.",
                  color: "teal"
                },
                {
                  insight: "UGC from college creators is the highest-leverage content play",
                  detail: "A student with 2,000 followers posting a genuine photo of the Cheeseburger reaches more potential customers in the trade area than a $500 ad buy. Personal promo codes track ROI. Double loyalty points incentivize posting. The content is free and more authentic than anything produced in-house. I know this works because I've seen the math on creator campaigns — it's ~$120/mo in food cost for 10K-50K impressions.",
                  color: "amber"
                },
                {
                  insight: "QR code tabletop tents are the zero-friction loyalty play",
                  detail: "Most restaurant loyalty programs fail because they require an app download or a long signup form. Nobody does that at a table. A QR code tent card at every table — scan, enter your phone, you're in — removes all friction. First reward is a free Chocolate Lava Cake (72% margin, $2.28 cost to the house). That's not a giveaway — it's a $2.28 investment to drive a full return visit. Pair it with an Instagram prompt for double points and you get UGC + loyalty acquisition in one move.",
                  color: "green"
                },
                {
                  insight: "A referral program is the cheapest customer acquisition channel that exists",
                  detail: "\"Bring a friend, you both get $5 off\" costs less than any ad. The referred customer already trusts the recommendation, so their conversion rate is 3-5x higher than a cold ad click. Toast's loyalty system supports referral codes natively — each member gets a unique code, and both parties earn points when it's redeemed. For students, this is especially powerful: one person tries Silver Spoon Social on Monday, shares the code in their group chat, and you've got a table of 4 next week. I flagged this because word-of-mouth is the one channel that compounds without increasing spend.",
                  color: "blue"
                },
                {
                  insight: "Turn the website into a PWA — put Lily's on every home screen next to DoorDash",
                  detail: "When a customer is hungry, they open whatever's on their home screen. Right now that's DoorDash (30% commission). A Progressive Web App puts Lily's ordering page on the home screen as an installable \"app\" — no App Store, no download, no $50K development budget. One tap from the browser and it's there forever. Pair it with the QR tabletop and every dine-in customer installs Lily's app on the spot. Push notifications for Monday specials at 4pm. The whole first-party recovery strategy clicks into place when you own the home screen real estate.",
                  color: "orange"
                },
              ].map((item, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 bg-${item.color}-500`} style={{ backgroundColor: { amber: '#f59e0b', green: '#22c55e', pink: '#ec4899', blue: '#3b82f6', purple: '#8b5cf6', red: '#ef4444', teal: '#14b8a6', orange: '#f97316' }[item.color] }} />
                    <div>
                      <p className="font-bold text-gray-900 text-sm mb-1">{item.insight}</p>
                      <p className="text-gray-600 text-xs leading-relaxed">{item.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What AI Helped With */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-gray-400"></div>
              <h3 className="font-bold text-gray-900 text-sm">Where AI helped — and where it didn't</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">AI assisted with:</p>
                <div className="space-y-2">
                  {[
                    "Formatting the analysis into a structured, presentation-ready layout",
                    "Building the interactive ROI calculator logic",
                    "Generating the PowerPoint export with matched styling",
                    "Writing out the full content calendar schedule",
                    "Structuring the FAQ answers for schema optimization",
                    "Compiling the technical SEO checklist items",
                    "Laying out the geo-targeted city page framework",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="text-gray-400 mt-0.5 flex-shrink-0">&#9679;</span>{item}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">AI did NOT generate:</p>
                <div className="space-y-2">
                  {[
                    "The core diagnosis (mismatch, margin bleed, loyalty drift, dead shifts)",
                    "The $ vs % margin insight — that came from reading the actual numbers",
                    "The farm-to-table angle — I noticed it in the restaurant concept",
                    "The geo-fencing and retargeting pixel strategy — my recommendation",
                    "The TikTok content formats — I flagged the gap and designed the concepts",
                    "The competitor audit framing — I pushed for the side-by-side",
                    "The decision to lead with data, not tactics — that's my approach",
                    "The PWA idea — I asked about making the site installable without app stores",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle size={10} className="text-amber-600 mt-0.5 flex-shrink-0" />{item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Philosophy */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-gray-800 text-sm leading-relaxed">
              <strong>My approach:</strong> I don't believe in running the same playbook for every restaurant. Lily's isn't a chain — it's a seasonal farm-to-table bistro with a 25-year history, a loyal (if shrinking) customer base, and a college campus next door. The marketing should feel like it comes from someone who eats there, not someone running ads from a dashboard. AI helps me build faster and present better — but the strategy, the insight, and the point of view are mine.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-gray-900 font-semibold text-sm">Sierra Scala</p>
            <p className="text-gray-500 text-xs">817.470.0933 | me@sierrascala.com</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/lilys" className="text-sm text-gray-700 hover:text-gray-900 font-medium flex items-center gap-1" data-testid="view-deck-link">View Slide Deck <ArrowRight size={14} /></a>
            <a href="/Lilys_Silver_Spoon_Case_Study_Sierra_Scala.pptx" download className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1" data-testid="download-pptx-link">Download .pptx <ArrowRight size={14} /></a>
            <button onClick={exportPDF} className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1" data-testid="footer-pdf-btn">
              <FileDown size={14} />
              Save as PDF
            </button>
            <a href="/" className="text-sm text-gray-400 hover:text-gray-600">Portfolio</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ToastStrategyPage;
